import React, {useEffect, useState} from 'react';
import {Col, Collapse, Container, Nav, Navbar, NavbarToggler, NavItem, Row} from 'reactstrap';
import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';
import Section, {SectionImage} from 'components/Elements/Section';
import IconList, {IconListItem} from 'components/Elements/IconList';
import {faArrowRight, faExclamationTriangle, faQuestionCircle} from '@fortawesome/pro-light-svg-icons';
import GuaranteeBox from 'components/Guarantee';
import ImgBullet, {ImgBulletCard} from 'components/Elements/ImgBullet';
import ModuleAccordion from 'components/ModuleAccordion';
import {modulesAds} from 'data/module.json';
import FaqList from 'components/FaqList';
import {faqAds} from 'data/faq.json';
import ReactMarkdown from 'react-markdown';
import {hhcollection} from 'data/hhcollection.json';
import {RecipeTileBox} from 'components/Elements/Tile';
import BonusList, {BonusAccordion, BonusOpen} from 'components/BonusList';
import {bonusAds} from 'data/bonus.json';
import ProductImage from 'components/Elements/ProductImage';
import {TestimonialBubble, TestimonialText} from 'components/Testimonial';
import useBalanceText from 'utils/useBalanceText';
import {SalesNav} from 'components/Elements/StickyPageNav';
import {Link} from 'react-scroll';
import classNames from 'classnames';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';
import TestimonialCarousel from 'components/TestimonialCarousel';

// @ts-ignore
import {enrollItems} from 'data/c-hhc.json';
import {InstructorCardList} from 'components/InstructorCardList';

export default function SalesCopy({version = 'sales', salesCta = '', variant = null, children, ...props}) {
  const {bucket, email} = useUserState();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      sales: salesSchedule = {
        phase: 'during',
        salesEnd: '',
        isSalesDay: false
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();

  useBalanceText();
  return (
    <>
      {version === 'sales' ? (
        <>
          <SectionImage
            id="truth"
            color="white"
            imgPosition="right"
            imgUrl={require('static/backgrounds/593309604.jpg')}
          >
            <Container>
              <Row>
                <Col>
                  <h2 className="section-heading">
                    You’ve probably heard, “Cut out processed meat,” and “Don’t smoke.”{' '}
                  </h2>
                  {variant === 'ad' ? (
                    <>
                      <p>
                        But are you taking all the right steps to protect your life, and the lives of your loved ones?
                      </p>
                      <p>Sadly, most people are not.</p>
                      <p>And it’s not their fault!</p>
                      <p>
                        Even though no one can live without a heart, few people ever get the crucial facts they need.
                      </p>
                      <h5 className="mt-0">
                        The truth is that even people who <i>think</i> they’re doing everything right are still missing
                        the most current scientific updates...
                      </h5>
                      <h5 className="mt-0">
                        <i>
                          Did you know that stress, loneliness, and depression can significantly affect heart health?
                        </i>
                      </h5>
                      <h5 className="mt-0">
                        <i>Or that recent “health” food fads are not the answer we were led to believe?</i>
                      </h5>
                      <p>And because we live in a world that mostly treats disease after it’s set in…</p>
                      <p>
                        Even the most well-meaning doctors may never provide the right information before it’s too late.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="balance-text">
                        {
                          {
                            prevent: (
                              <>
                                But are you taking the right steps to stop heart disease from damaging your life, and
                                the lives of your loved ones?
                              </>
                            ),
                            optimize: (
                              <>
                                But are you taking the right steps to optimize your cardiovascular health, so you can
                                reach your potential?
                              </>
                            ),
                            reverse: (
                              <>
                                But are you taking the right steps to stop heart disease from threatening your life, and
                                the lives of your loved ones?
                              </>
                            )
                          }[bucket]
                        }
                      </p>
                      <p>Sadly, most people are not.</p>
                      <p>And it’s not their fault!</p>
                      <p>
                        Even though no one can live without a heart, few people ever get the crucial facts they need.
                      </p>
                      {
                        {
                          prevent: (
                            <h5 className="mt-0">
                              The truth is that even people who <i>think</i> they’re doing everything right can still
                              fall victim to this deadly epidemic.
                            </h5>
                          ),
                          optimize: (
                            <>
                              <h5 className="mt-0">
                                The truth is that even people who <i>think</i> they’re doing everything right are still
                                missing the most current scientific updates...
                              </h5>
                              <h5 className="mt-0">
                                <i>
                                  Did you know that stress, loneliness, and depression significantly affect your heart
                                  health?
                                </i>
                              </h5>
                              <h5 className="mt-0">
                                <i>Or that recent “health” food fads are not the miracle we were led to believe?</i>
                              </h5>
                            </>
                          ),
                          reverse: (
                            <h5 className="mt-0">
                              The truth is that even people who follow their doctor’s advice to a “T” may still be
                              headed towards a lifetime of worsening symptoms and non-stop fear.
                            </h5>
                          )
                        }[bucket]
                      }
                      <p>And because we live in a world that mostly treats disease after it’s set in...</p>
                      {
                        {
                          prevent: (
                            <p>
                              Even the most well-meaning doctors may never arm you with the right information to prevent
                              heart disease before it’s too late.
                            </p>
                          ),
                          optimize: (
                            <p>
                              Even the most well-meaning doctors may never arm you with the right information to get
                              your heart in the best shape possible before it’s too late.
                            </p>
                          ),
                          reverse: (
                            <p>
                              Even the most well-meaning doctors may never arm you with the right information to prevent
                              heart disease before it’s too late.
                            </p>
                          )
                        }[bucket]
                      }
                    </>
                  )}
                </Col>
              </Row>
            </Container>
          </SectionImage>

          <SectionImage
            id="wondering"
            color="light-gray"
            imgPosition="left"
            imgUrl={require('static/backgrounds/1212263337.jpg')}
          >
            <Container>
              <Row>
                <Col>
                  <h2 className="section-heading">Maybe you’re wondering...</h2>

                  <IconList iconColor="purple">
                    {variant === 'ad' ? (
                      <>
                        <IconListItem icon={faQuestionCircle}>What can I do to keep my heart healthy?</IconListItem>
                        <IconListItem icon={faQuestionCircle}>
                          Might I need to take prescription medications for the rest of my life?
                        </IconListItem>
                        <IconListItem icon={faQuestionCircle}>
                          What if I’ve smoked or eaten a lot of fried foods in the past? Am I doomed or can I turn it
                          around?
                        </IconListItem>
                        <IconListItem icon={faQuestionCircle}>
                          I know that extra weight is a risk factor. How can I lose weight safely and keep it off?
                        </IconListItem>
                        <IconListItem icon={faQuestionCircle}>
                          What if I am doing everything ‘right’, but my doctor is still concerned?
                        </IconListItem>
                        <IconListItem icon={faQuestionCircle}>What foods do I need to eat or avoid?</IconListItem>
                        <IconListItem icon={faQuestionCircle}>
                          How much exercise do I need for my age and body type? And what really counts as exercise… is
                          yoga enough?
                        </IconListItem>
                        <IconListItem icon={faQuestionCircle}>
                          I’ve heard about stress being bad for my heart, but do other emotions like grief, sadness, and
                          resentment also matter?
                        </IconListItem>
                        <IconListItem icon={faQuestionCircle}>What else am I missing?</IconListItem>
                      </>
                    ) : (
                      <>
                        {
                          {
                            prevent: (
                              <>
                                <IconListItem icon={faQuestionCircle}>
                                  What can I do to prevent heart disease?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  Might I need to take prescription medications for the rest of my life?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  What if I’ve smoked or eaten a lot of fried foods in the past? Am I doomed for heart
                                  problems or can I turn it around?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  I know excessive weight is a major risk factor. Can I lose weight without punishing
                                  workouts?
                                </IconListItem>
                              </>
                            ),
                            optimize: (
                              <>
                                <IconListItem icon={faQuestionCircle}>
                                  What can I do to get my heart in top shape?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  What free things can I do to make sure my heart is at its peak?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  What should I be doing daily to stay active for as long as I can?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  I know that even a little extra weight will slow me down. How can I lose weight
                                  confidently and safely?
                                </IconListItem>
                              </>
                            ),
                            reverse: (
                              <>
                                <IconListItem icon={faQuestionCircle}>
                                  What can I do to reverse heart disease?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  What free things can I do to turn symptoms around?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  What do I do if I’m already taking prescription medication? Is it even possible to
                                  reduce or eliminate them?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  What can I do to get from disease to health?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  What else can I do if I am doing everything ‘right’, and still have elevated LDL
                                  cholesterol, or high blood pressure?
                                </IconListItem>
                                <IconListItem icon={faQuestionCircle}>
                                  I know extra weight will make things worse. How can I lose weight without punishing
                                  workouts?
                                </IconListItem>
                              </>
                            )
                          }[bucket]
                        }
                        <IconListItem icon={faQuestionCircle}>What foods do I need to eat or avoid?</IconListItem>
                        <IconListItem icon={faQuestionCircle}>
                          How much exercise do I need for my age and body type? And what really counts as exercise... is
                          yoga enough?
                        </IconListItem>
                        <IconListItem icon={faQuestionCircle}>
                          I’ve heard about stress being bad for my heart, but do other emotions like grief, sadness, and
                          resentment also matter?
                        </IconListItem>
                        <IconListItem icon={faQuestionCircle}>What am I missing?</IconListItem>
                      </>
                    )}
                  </IconList>
                </Col>
              </Row>
            </Container>
          </SectionImage>

          <SectionImage
            id="modern-medicine"
            color="white"
            imgPosition="right"
            imgUrl={require('static/backgrounds/1150397417.jpg')}
            className="section-noangle"
          >
            <Container>
              <Row>
                <Col>
                  <h2 className="section-heading">Modern Medicine is Failing Us</h2>
                  {variant === 'ad' ? (
                    <>
                      <p>
                        Despite trillions of dollars in medical spending, heart health issues are on the rise — claiming
                        the lives of people <i>decades</i> before their time.
                      </p>
                      <p>
                        Even though diet and lifestyle are the foundation of health, hospitals still serve burgers to
                        patients who are recovering from surgery.
                      </p>
                      <p>
                        And with all the focus on treating the symptoms, painfully little attention is given to helping
                        you stay healthy in the first place.
                      </p>
                      <p>People are robbed of enjoying their golden years because of:</p>
                      <IconList iconColor="yellow" className="mb-0">
                        <IconListItem icon={faExclamationTriangle}>High stress...</IconListItem>
                        <IconListItem icon={faExclamationTriangle}>The wrong foods...</IconListItem>
                        <IconListItem icon={faExclamationTriangle}>Extra weight...</IconListItem>
                        <IconListItem icon={faExclamationTriangle}>Toxins in household products...</IconListItem>
                        <IconListItem icon={faExclamationTriangle}>Pollution and pesticides...</IconListItem>
                        <IconListItem icon={faExclamationTriangle}>A sedentary lifestyle...</IconListItem>
                        <IconListItem icon={faExclamationTriangle}>
                          Even a lack of gratitude and spiritual connection...
                        </IconListItem>
                      </IconList>
                    </>
                  ) : (
                    <>
                      {
                        {
                          prevent: (
                            <>
                              <p>
                                Despite trillions of dollars in medical spending, heart attacks, high cholesterol, and
                                obesity are on the rise — claiming the lives of people <i>decades</i> before their time.
                              </p>
                              <p>
                                People are robbed of enjoying their golden years because of the factors that lead to
                                heart disease, including:
                              </p>
                              <IconList iconColor="yellow" className="mb-0">
                                <IconListItem icon={faExclamationTriangle}>High stress...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>The wrong foods...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>Extra weight...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>
                                  Toxins in household products...
                                </IconListItem>
                                <IconListItem icon={faExclamationTriangle}>Pollution and pesticides...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>A sedentary lifestyle...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>
                                  Even a lack of gratitude and spiritual connection...
                                </IconListItem>
                              </IconList>
                            </>
                          ),
                          optimize: (
                            <>
                              <p>
                                Despite advances in Western medicine, it falls short when it comes to truly optimizing
                                your heart health.
                              </p>
                              <p>
                                Doctors are trained to prescribe expensive drugs and surgeries, but most have learned
                                next to nothing about nutrition.
                              </p>
                              <p>
                                Even though diet and lifestyle are the foundation of health, hospitals still serve
                                burgers to patients who are recovering from bypass surgery.
                              </p>
                              <p>
                                And with all the focus on treating the symptoms of disease, painfully little attention
                                is given to helping you enjoy the health and vitality you deserve.
                              </p>
                              <p>
                                Even if you’re doing a lot of things right, you might be missing one or more of the keys
                                to heart health due to misinformation and slick marketing.
                              </p>
                              <IconList iconColor="yellow" className="mb-0">
                                <IconListItem icon={faExclamationTriangle}>Staying stressed...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>
                                  Eating a few (or a lot) of the wrong foods...
                                </IconListItem>
                                <IconListItem icon={faExclamationTriangle}>
                                  Keeping toxic household products around...
                                </IconListItem>
                                <IconListItem icon={faExclamationTriangle}>
                                  Ingesting food with pesticides...
                                </IconListItem>
                                <IconListItem icon={faExclamationTriangle}>
                                  Exercising in a way that causes inflammation (the root of most disease)...
                                </IconListItem>
                              </IconList>
                            </>
                          ),
                          reverse: (
                            <>
                              <p>
                                Despite trillions of dollars in medical spending, heart attacks, high cholesterol, and
                                obesity are on the rise — claiming the lives of people <i>decades</i> before their time.
                              </p>
                              <p>
                                People are robbed of enjoying their golden years because of the factors that lead to
                                heart disease, including:
                              </p>
                              <IconList iconColor="yellow" className="mb-0">
                                <IconListItem icon={faExclamationTriangle}>High stress...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>The wrong foods...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>Extra weight...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>
                                  Toxins in household products...
                                </IconListItem>
                                <IconListItem icon={faExclamationTriangle}>Pollution and pesticides...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>A sedentary lifestyle...</IconListItem>
                                <IconListItem icon={faExclamationTriangle}>
                                  Even a lack of gratitude and spiritual connection...
                                </IconListItem>
                              </IconList>
                            </>
                          )
                        }[bucket]
                      }
                    </>
                  )}
                </Col>
              </Row>
            </Container>
          </SectionImage>

          <Section id="modern-medicine-b" color="white">
            <Container>
              <Row>
                <Col xl="11">
                  {variant === 'ad' ? (
                    <>
                      <p>
                        But there’s <i>great</i> news.
                      </p>
                      <p>
                        A breakthrough study called the Lifestyle Heart Trial (which Dr. Mimi helped to implement)
                        showed that when patients made a few lifestyle changes for one year, 82% of participants were
                        able to experience significant changes.
                      </p>
                      <p>
                        Dozens of subsequent studies have shown how powerful lifestyle is to keep hearts healthy!
                        <sup>(1)</sup>
                      </p>
                    </>
                  ) : (
                    <>
                      {
                        {
                          prevent: (
                            <>
                              <p>
                                But there’s <i>great</i> news.
                              </p>
                              <p>
                                A breakthrough study called the Lifestyle Heart Trial (which Dr. Mimi helped to
                                implement) showed that when heart disease patients made a few lifestyle changes for one
                                year, 82% of participants were able to partially or fully REVERSE their heart disease.
                                And dozens of subsequent studies have shown how powerful lifestyle is to prevent heart
                                disease in the first place!<sup>(1)</sup>
                              </p>
                              <p>That means it’s not too late to protect yourself from heart disease!</p>
                            </>
                          ),
                          optimize: (
                            <>
                              <p>
                                These seemingly small things add up and could be keeping you from high energy levels and
                                a strong heart you want.
                              </p>
                              <p>This is a big deal — there are so many things you want to do now and in the future!</p>
                            </>
                          ),
                          reverse: (
                            <>
                              <p>
                                But there’s <i>great</i> news.
                              </p>
                              <p>
                                A breakthrough study called the Lifestyle Heart Trial (which Dr. Mimi helped to
                                implement) showed that when heart disease patients made a few lifestyle changes for one
                                year, 82% of participants were able to partially or fully REVERSE their heart disease.
                                <sup>(1)</sup>
                              </p>
                              <p>That means it’s not too late to turn your heart health around!</p>
                            </>
                          )
                        }[bucket]
                      }
                    </>
                  )}
                </Col>
              </Row>
            </Container>
          </Section>

          <SectionImage
            id="critical-fact"
            color="light-gray"
            imgPosition="left"
            imgUrl={require('static/backgrounds/1219794455.jpg')}
            title={
              <>
                <h2 className="section-heading mb-4">
                  And there’s one other slightly mysterious, unique, and absolutely critical fact about{' '}
                  {variant === 'ad' ? 'the' : 'your'} heart:
                </h2>
              </>
            }
            titleClass="text-center mb-0"
          >
            <Container>
              <Row>
                <Col>
                  <h4 className="section-heading">
                    <i>
                      {variant === 'ad' ? 'Emotions' : 'Your emotions'} have an overwhelming impact on your heart
                      health.
                    </i>
                  </h4>
                  <IconList iconColor="purple">
                    <IconListItem icon={faArrowRight}>Anger</IconListItem>
                    <IconListItem icon={faArrowRight}>Resentment</IconListItem>
                    {variant === 'ad' ? null : <IconListItem icon={faArrowRight}>Depression</IconListItem>}
                    <IconListItem icon={faArrowRight}>Loneliness</IconListItem>
                    <IconListItem icon={faArrowRight}>Unhappiness</IconListItem>
                    <IconListItem icon={faArrowRight}>Guilt</IconListItem>
                    <IconListItem icon={faArrowRight}>Grief</IconListItem>
                    <IconListItem icon={faArrowRight}>Shame</IconListItem>
                  </IconList>
                  {variant === 'ad' ? (
                    <p>
                      How we respond to these emotions plays a KEY role in determining whether we’ll be able to keep
                      exercising, playing sports, keeping up with family and friends, and having the fun life you’re
                      meant to have!
                    </p>
                  ) : (
                    <>
                      {
                        {
                          prevent: (
                            <>
                              <p>
                                How you respond to these emotions plays a KEY role in determining whether you’ll succumb
                                to a life of chest pain, angioplasties, multiple medications, and anxiety-ridden lab
                                tests...
                              </p>
                              <p>...Or you can live a vibrant, energized life. It really can be up to you.</p>
                            </>
                          ),
                          optimize: (
                            <p>
                              How you respond to these emotions plays a KEY role in determining whether you’ll be able
                              to keep exercising, playing sports, keeping up with family and your friends, and having
                              the fun life you’re meant to have!
                            </p>
                          ),
                          reverse: (
                            <>
                              <p>
                                How you respond to these emotions plays a KEY role in determining whether you’ll
                                continue to live a life with increasing chest pain, angioplasties, medications, and
                                anxiety-ridden lab tests...
                              </p>
                              <p>Or you can live a vibrant, energized life. It really can be up to you.</p>
                            </>
                          )
                        }[bucket]
                      }
                    </>
                  )}
                </Col>
              </Row>
            </Container>
          </SectionImage>

          <SectionImage
            id="genetics"
            color="white"
            imgPosition="right"
            imgUrl={require('static/backgrounds/929537264.jpg')}
          >
            <Container>
              <Row>
                <Col>
                  <h2 className="section-heading">While it’s true that genetics play a part...</h2>
                  {variant === 'ad' ? (
                    <>
                      <p>
                        The newest scientific studies — along with concrete evidence from millions of people — show us
                        that in most cases our genes are NOT our fate.
                      </p>
                      <h5 className="mt-0">Genetics are only a minuscule part of the equation.</h5>
                      <h5 className="mt-0">Even with early warning signs or a family history!</h5>
                    </>
                  ) : (
                    <>
                      {
                        {
                          prevent: (
                            <>
                              <p>
                                The newest scientific studies — along with concrete evidence from millions who’ve taken
                                the right steps to avoid heart disease — show us that in most cases your genes are NOT
                                your fate.
                              </p>
                              <p className="balance-text">
                                <b>Genetics are only a minuscule part of the equation.</b>
                              </p>
                              <h5 className="mt-0">
                                Want the best news? Heart disease is not just preventable — it’s often reversible.
                              </h5>
                              <h5 className="mt-0">
                                Even if you have the early warning signs or a history of heart disease in your family!
                              </h5>
                            </>
                          ),
                          optimize: (
                            <>
                              <p>
                                The newest scientific studies — along with concrete evidence from millions who’ve taken
                                the right steps to power their heart — show us that in most cases your genes are NOT
                                your fate.
                              </p>
                              <p>
                                <b>Genetics are only a minuscule part of the equation.</b>
                              </p>
                              <h5 className="mt-0">Want the best news?</h5>
                              <h5 className="mt-0">
                                Maximizing your heart health doesn’t take a ton of expensive supplements, brutal
                                routines, or a lifetime of tasteless rabbit food!
                              </h5>
                            </>
                          ),
                          reverse: (
                            <>
                              <p>
                                The newest scientific studies — along with concrete evidence from millions of people who
                                have reversed their symptoms — show us that in most cases your genes are NOT your fate.
                              </p>
                              <p>
                                <b>Genetics are only a minuscule part of the equation.</b>
                              </p>
                              <h5 className="mt-0">
                                Want the best news? Heart disease is not just preventable — it’s often reversible.
                              </h5>
                              <h5 className="mt-0">
                                Even if you have the early warning signs or a history of heart disease in your family!
                              </h5>
                            </>
                          )
                        }[bucket]
                      }
                    </>
                  )}
                </Col>
              </Row>
            </Container>
          </SectionImage>
        </>
      ) : null}

      <Section id="imagine" color="light-gray">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading">Imagine having everything you need to:</h2>
              <ImgBulletCard img={require('static/backgrounds/1167431824.jpg')}>
                {variant === 'ad' ? (
                  <>End the confusion and fear…</>
                ) : (
                  <>
                    End the confusion and fear <br className="d-none d-sm-block d-lg-none" />
                    that gnaws at you…
                  </>
                )}
              </ImgBulletCard>
              {variant === 'ad' ? null : (
                <ImgBulletCard img={require('static/backgrounds/1174972796.jpg')}>
                  Prevent an untimely death, for you <br className="d-none d-lg-block d-xl-none" />
                  or your loved ones
                </ImgBulletCard>
              )}
              <ImgBulletCard img={require('static/backgrounds/1199853544.jpg')}>
                {variant === 'ad' ? (
                  <>
                    Keep up with friends and family <br className="d-none d-sm-block d-md-none" />
                    in your <br className="d-none d-lg-block d-xl-none" />
                    60s, 70s, 80s, <br className="d-none d-xl-block" />
                    and <br className="d-none d-sm-block d-md-none" />
                    even in your 90s
                  </>
                ) : (
                  <>
                    Keep up with your friends and family in your <br className="d-none d-lg-block d-xl-none" />
                    60s, 70s, 80s, <br className="d-none d-sm-block d-lg-none" />
                    and even in your 90s
                  </>
                )}
              </ImgBulletCard>
              <ImgBulletCard img={require('static/backgrounds/1065782694.jpg')}>
                {variant === 'ad' ? (
                  <>
                    Ditch a future of medical and <br className="d-none d-lg-block d-xl-none" />
                    insurance nightmares
                  </>
                ) : (
                  <>
                    Ditch a future of medications, ineffective supplements, visits to the cardiologist, surgeries, and
                    insurance headaches
                  </>
                )}
              </ImgBulletCard>
              <ImgBulletCard img={require('static/backgrounds/1135940064.jpg')}>
                Experience more love, connection, spiritual fulfillment, and joy — all of which can help{' '}
                <br className="d-none d-sm-block d-lg-none" />
                to heal {variant === 'ad' ? 'the' : 'your'} heart
              </ImgBulletCard>
              <ImgBulletCard img={require('static/backgrounds/1191275124.jpg')}>
                {variant === 'ad' ? (
                  <>
                    Enjoy the piece of mind having the right information to take the right actions{' '}
                    <br className="d-none d-lg-block d-xl-none" />
                    for heart health
                  </>
                ) : (
                  <>Enjoy the piece of mind that comes with knowing you’re doing all you should be doing</>
                )}
              </ImgBulletCard>
              <ImgBulletCard img={require('static/backgrounds/1010069056.jpg')}>
                {variant === 'ad' ? (
                  <>
                    Eat the best food to <br className="d-none d-sm-block d-lg-none" />
                    shed excess weight
                  </>
                ) : (
                  <>
                    Shed any extra weight <br className="d-none d-sm-block d-lg-none" />
                    that might be slowing <br className="d-none d-lg-block d-xl-none" />
                    you <br className="d-none d-sm-block d-lg-none" />
                    down — and that can increase your risk <br className="d-none d-lg-block d-xl-none" />
                    of heart disease
                  </>
                )}
              </ImgBulletCard>
              <ImgBulletCard img={require('static/backgrounds/1273860602.jpg')}>
                Understand {variant === 'ad' ? 'the' : 'your'} heart’s connection to{' '}
                <br className="d-none d-xl-block" />
                your body, <br className="d-none d-sm-block d-lg-none" />
                mind, and spirit
              </ImgBulletCard>
              <ImgBulletCard img={require('static/backgrounds/1270066435.jpg')}>
                {variant === 'ad' ? (
                  <>Feel secure with the insights into a holistic approach that’s helped millions of people thrive</>
                ) : (
                  <>
                    Feel secure knowing you <br className="d-none d-sm-block d-lg-none" />
                    have insight into a holistic approach that’s helped millions{' '}
                    <br className="d-none d-sm-block d-lg-none" />
                    of people thrive
                  </>
                )}
              </ImgBulletCard>
              <ImgBulletCard img={require('static/backgrounds/917337130.jpg')}>
                {variant === 'ad' ? (
                  <>
                    Have the most current scientific information <br className="d-none d-lg-block d-xl-none" />
                    in your corner
                  </>
                ) : (
                  <>
                    Trust that you have the most current scientific <br className="d-none d-xl-block" />
                    information <br className="d-none d-sm-block d-lg-none" />
                    in your corner
                  </>
                )}
              </ImgBulletCard>
              {variant === 'ad' ? null : (
                <ImgBulletCard img={require('static/backgrounds/1297543648.jpg')}>
                  And leave your annual check-up after your doctor <br className="d-none d-xl-block" />
                  marvels at how healthy your heart is and says, <br className="d-none d-xl-block" />
                  “You’re in amazing shape!”
                </ImgBulletCard>
              )}
              <h3 className="mt-3 text-center">All this is possible{variant === 'ad' ? null : ' for you'}</h3>
              <p className="text-center">
                But it’s urgent. Don’t wait to implement these breakthrough, <br className="d-none d-lg-block" />
                science-based, and natural solutions for heart health.
              </p>
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="introduction" color="white">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <p className="mb-2">
                <i>Introducing</i>
              </p>
              <p className="mb-4">
                <img className="img-course-logo" src="https://cdn.foodrevolution.org/hhc/hhc-logo.svg" />
              </p>
              <p className="balance-text mb-0">
                Gain energy, eat better, stress less, and love more — for your heart's sake!
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg="10">
              <h3 className="section-heading">
                {variant === 'ad' ? (
                  <>
                    <i>Healthy Heart</i> is for anyone who wants to:
                  </>
                ) : (
                  <>
                    <i>Healthy Heart</i> is for you if you want to:
                  </>
                )}
              </h3>
              <IconList>
                {variant === 'ad' ? (
                  <>
                    <IconListItem>Have more time, energy, and motivation to do the things you love.</IconListItem>
                    <IconListItem>Be healthy and spend more quality time with family and friends.</IconListItem>
                    <IconListItem>
                      Discover the impressive ways your heart stores emotions and how to reduce stress, sadness and
                      anger — and instead become one of the happiest people you know. This is based on evidence and the
                      latest research!
                    </IconListItem>
                    <IconListItem>
                      Eat better, lose any excess weight, and feel more confident - without feeling deprived or stuck
                      with a diet of bland food.
                    </IconListItem>
                    <IconListItem>
                      Enjoy healthy, plant-based recipes based on delicious foods that can help keep the heart happy.
                    </IconListItem>
                    <IconListItem>
                      Ensure you’re not missing any of the key elements that help to optimize your heart health — and
                      continue to enjoy favorite activities without fear of slowing down prematurely.
                    </IconListItem>
                    <IconListItem>
                      Find a community of supportive, health-minded friends who encourage you to make healthy choices
                      and cheer you on.
                    </IconListItem>
                    <IconListItem>
                      Get answers to heart health questions whenever you wonder about nutrition, exercise, zapping
                      stress, how to get back on track, or even for just a little inspiration.
                    </IconListItem>
                    <IconListItem>Protect yourself and your family from unnecessary heartache.</IconListItem>
                  </>
                ) : (
                  <>
                    <IconListItem>
                      {
                        {
                          prevent: (
                            <>
                              Stop heart disease from derailing your life — so you can have more energy and motivation
                              to do the things you love.
                            </>
                          ),
                          optimize: (
                            <>
                              Take advantage of every minute of your life, with more energy and motivation to do the
                              things you love.
                            </>
                          ),
                          reverse: (
                            <>
                              Stop heart disease from derailing your life — so you can have more energy and motivation
                              to do the things you love.
                            </>
                          )
                        }[bucket]
                      }
                    </IconListItem>
                    <IconListItem>
                      Be your healthiest so you can add decades of quality time with your family and friends.
                    </IconListItem>
                    <IconListItem>
                      Discover the impressive ways your heart stores emotions and how to reduce stress, depression, and
                      anger — and instead become one of the happiest people you know. This is based on evidence and the
                      latest research!
                    </IconListItem>
                    <IconListItem>
                      Eat better, lose any excess weight, and feel more confident in your choices without ever feeling
                      deprived or stuck with a diet of bland food.
                    </IconListItem>
                    <IconListItem>
                      {
                        {
                          prevent: (
                            <>
                              Enjoy healthy, plant-based recipes based on foods that can help you slash your risk of
                              heart disease.
                            </>
                          ),
                          optimize: (
                            <>
                              Enjoy healthy, plant-based recipes based on foods that can help you give your heart every
                              advantage.
                            </>
                          ),
                          reverse: (
                            <>
                              Enjoy healthy, plant-based recipes based on foods that can help you prevent and even
                              reverse heart disease.
                            </>
                          )
                        }[bucket]
                      }
                    </IconListItem>
                    <IconListItem>
                      {
                        {
                          prevent: (
                            <>
                              Ensure you’re not missing any of the key elements that help you to optimize your heart
                              health — and enjoy being able to do the activities you love without fear of heart trouble
                              slowing you down prematurely.
                            </>
                          ),
                          optimize: (
                            <>
                              Ensure you’re not missing any of the key elements that help you to optimize your heart
                              health… and enjoy the activities you love without fear of heart trouble slowing you down
                              prematurely.
                            </>
                          ),
                          reverse: (
                            <>
                              Ensure you’re not missing any of the key elements that help you to optimize your heart
                              health — and enjoy being able to do the activities you love without heart trouble robbing
                              you of quality years.
                            </>
                          )
                        }[bucket]
                      }
                    </IconListItem>
                    <IconListItem>
                      Find a community of supportive, health-minded friends who encourage you to make healthy choices
                      and cheer you on.
                    </IconListItem>
                    <IconListItem>
                      Get answers to your heart health questions whenever you wonder about nutrition, dangerous oils,
                      exercise, zapping stress, how to get back on track, or even when you need a little inspiration.
                    </IconListItem>
                    <IconListItem>Protect yourself and your family from unnecessary heartache.</IconListItem>
                  </>
                )}
              </IconList>
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="testimonials-1a" color="light-gray">
        <Container>
          <Row className="justify-content-center">
            <Col lg="10">
              <TestimonialBubble bgColor="white" id={variant === 'ad' ? 'hhc-lori-r-ad' : 'hhc-lori-r'} />
              <hr className="my-5 border-black-50" />
              <TestimonialBubble bgColor="white" id="hhc-laura" />
              <hr className="my-5 border-black-50" />
              <TestimonialBubble bgColor="white" id="hhc-karen-e" />
              <hr className="my-5 border-black-50" />
              <TestimonialBubble bgColor="white" id={variant === 'ad' ? 'hhc-delores-r-ad' : 'hhc-delores-r'} />
              <hr className="my-5 border-black-50" />
              <TestimonialBubble bgColor="white" id="jorge-m" />
              <hr className="my-5 border-black-50" />
              <TestimonialBubble bgColor="white" id="cheryl-c" />
              <hr className="my-5 border-black-50" />
              <TestimonialBubble bgColor="white" id="badiyyah-w" />
              <p className="text-small text-center mt-5 mb-0">
                <i>
                  These stories are from people who’ve found success with our programs. Each of them is unique. And so
                  are you! Your experiences will vary, of course. And no, we never offer compensation for these
                  statements!
                </i>
              </p>
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="testimonials-1b" color="white">
        <Container>
          <Row className="justify-content-center">
            <Col lg="10">
              <TestimonialBubble id="deepak-c" />
              <hr className="my-5 border-black-50" />
              <TestimonialBubble id="andrew-w" />
              <hr className="my-5 border-black-50" />
              <TestimonialBubble id="larry-d" />
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="course-content" color="light-gray">
        <Container>
          <Row className="text-center">
            <Col>
              <h2 className="section-heading">
                What’s Included In <i>Healthy Heart</i>
              </h2>
              <p className="balance-text">
                {variant === 'ad' ? (
                  <>
                    Get everything needed to help nourish cardiovascular health with <i>Healthy Heart.</i>
                  </>
                ) : (
                  <>
                    Now you can get everything you need to help protect and nourish your cardiovascular health with{' '}
                    <i>Healthy Heart.</i>
                  </>
                )}
              </p>
              <Row className="d-flex align-items-center justify-content-center mt-4">
                <Col xs="12" lg="10">
                  <ProductImage />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg="10">
              <IconList>
                <IconListItem>
                  6 modules, each with 6 no fluff, easily digestible 5–20 minute video lessons
                </IconListItem>
                <IconListItem>
                  6 recipe demonstration lessons showing exactly how to prepare some of Dr. Guarneri’s heart-healthy
                  favorites
                </IconListItem>
                <IconListItem>
                  All lessons available in HD video, MP3 audio, and with downloadable transcripts
                </IconListItem>
                <IconListItem>
                  Access to an exclusive community forum (customized for course participants and NOT on Facebook)
                </IconListItem>
                <IconListItem>Health Coaches and Certified Nutritionists to answer your questions</IconListItem>
                <IconListItem>
                  36 delightful plant-based, non-GMO recipes that are super easy to make, with gluten-free and soy-free
                  options
                </IconListItem>
                <IconListItem>
                  Downloadable Healthy Heart Coursebook to help you track your progress and implement everything you
                  learn
                </IconListItem>
                <IconListItem>
                  Q&A with Dr. Guarneri, hosted by Ocean Robbins – where we answer the TOP questions from our community
                </IconListItem>
                <IconListItem>
                  6 Free Bonus Expert Interviews with Dr. Dean Ornish, Dr. Kim Williams, Dr. Joel Kahn, Dr. Saray
                  Stancic, Dr. Aysha Akhtar, and Marco Borges
                </IconListItem>
                <IconListItem>Over $400 in valuable bonus gifts that you’ll actually use!</IconListItem>
                {version === 'screening' ? (
                  <IconListItem>
                    PLUS — One-time offer — from this page only: Get a special 50% off discount & free Heart Health
                    Collection with extra interviews and recipes (a $97 bonus gift)
                  </IconListItem>
                ) : null}
              </IconList>
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="cta-1" color="gradient-indigo-purple-right">
        <Container>{children}</Container>
      </Section>

      <Section id="course-details-title" color="green" innerClass="text-center text-white">
        <Container>
          <Row>
            <Col>
              <h2 className="section-only-heading text-uppercase">The Course Details</h2>
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="course-details-lessons" color="white">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-center">36 In-Depth Video Lessons on All Things Heart Health</h2>
            </Col>
          </Row>
          <Row className="justify-content-center text-center">
            <Col lg="10" xl="8">
              <img src="https://cdn.foodrevolution.org/hhc/hhc-videos.png" style={{maxHeight: '350px'}} />
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg="10">
              <p>
                {variant === 'ad' ? (
                  <>
                    Heart health goes way beyond diet and exercise habits (and most mainstream practitioners are only
                    trained to give out pills). We believe in an integrative approach to heart health that most doctors’
                    offices don’t share — and probably don’t even know about.
                  </>
                ) : (
                  <>
                    Your heart health goes way beyond your diet and exercise habits (and most mainstream practitioners
                    are only trained to give out pills). We believe in an integrative approach to heart health that you
                    won’t hear about in most doctors’ offices.
                  </>
                )}
              </p>
              <p>
                Get 6 video lessons in each of the 6 modules (most with run times from 5–20 minutes), taught by Dr. Mimi
                Guarneri, President of the Academy of Integrative Health and Medicine.
              </p>
              <p>
                It’s super easy to absorb everything. Like Netflix, but better, since this information could literally{' '}
                {variant === 'ad' ? 'change' : 'save'} your life.
              </p>
            </Col>
          </Row>
        </Container>
      </Section>
      <Section id="course-details-coaches" color="light-gray">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-center mb-0">
                {variant === 'ad' ? (
                  <>Take Control of Heart Health with Our Certified Health Coaches & Nutritionists</>
                ) : (
                  <>
                    Take Control of Heart Health and Reduce Your Risk with Our Certified Health Coaches & Nutritionists
                  </>
                )}
              </h2>
            </Col>
          </Row>
          <Row className="justify-content-center text-center">
            <Col lg="10" xl="8">
              <img className="my-4" src={require('static/course-details-coaches.png')} />
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg="10">
              <p>
                A life of statins, surgery, and {variant === 'ad' ? 'fear' : 'anxiety'} that things will only get
                worse... Many people with a family history{variant === 'ad' ? null : ' of heart problems'} feel like
                their fate begins and ends with their doctor.
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    How different things become when you realize <b>you</b> have the power!
                  </>
                ) : (
                  <>How different things become when you realize you have the power to improve your own health!</>
                )}
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    Our certified health and nutrition coaches are ready to help with tips and guidance to support heart
                    health. Get ongoing support any time a question or new health concern comes up — even after the
                    course is over!
                  </>
                ) : (
                  <>
                    Our certified health and nutrition coaches are ready to help you so you can protect your heart and
                    feel amazing! That’s why we included a way for you to have ongoing support any time a question or
                    new health concern comes up — even after the course is over!
                  </>
                )}
              </p>
            </Col>
          </Row>
        </Container>
      </Section>
      <Section id="course-details-recipes" color="white">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-center">
                36 of Dr. Guarneri’s favorite heart-healthy plant-based recipes
              </h2>
            </Col>
          </Row>
          <Row className="text-center">
            <Col>
              <p className="balance-text">
                Learn how to cook nourishing plant-based meals with recipes and expert demos from Food Revolution
                Network’s Registered Dietitian Nutritionist.
              </p>
              <p className="balance-text">
                Each module has a cooking demo video, along with 6 easy, heart-healthy dishes including…
              </p>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col lg="6" className="mb-4">
              <RecipeTileBox id="avocado-salad" />
            </Col>
            <Col lg="6" className="mb-4">
              <RecipeTileBox id="zesty-turmeric-ginger-carrot-cake" />
            </Col>
            <Col lg="6" className="mb-4">
              <RecipeTileBox id="mushroom-and-pepper-scramble-tofu" />
            </Col>
            <Col lg="6" className="mb-4">
              <RecipeTileBox id="spiced-moroccan-vegetable-soup" />
            </Col>
            <Col lg="6" className="mb-4 mb-lg-0">
              <RecipeTileBox id="fluffy-buckwheat-chia-pancakes" />
            </Col>
            <Col lg="6" className="mb-0">
              <RecipeTileBox id="chickpeas-and-peas-vegan-curry" />
            </Col>
          </Row>
          <Row>
            <Col>
              <h3 className="text-center">And so many more!</h3>
            </Col>
          </Row>
        </Container>
      </Section>
      <SectionImage
        id="course-details-community"
        color="light-gray"
        imgPosition="left"
        imgUrl={require('static/backgrounds/1188864563.jpg')}
      >
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading">An Online Community Like No Other</h2>
              <p>
                {variant === 'ad' ? (
                  <>None of us can keep up with heart-happy lifestyle changes without the right social support.</>
                ) : (
                  <>
                    Time for the bad news: you won’t keep up with your heart-happy lifestyle changes without the right
                    social support.
                  </>
                )}
              </p>
              <p>
                The upside? {variant === 'ad' ? 'Find' : 'You’ll find'} your people inside the{' '}
                <i>Healthy Heart Course.</i>
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    Join a community who will talk you down from the cookie binge and help you navigate the meat and
                    potatoes guilt trips from your friends and family. They “get” your fears (‘cause they’ve got them
                    too). And they’ll be there with the confetti for every one of your wins.
                  </>
                ) : (
                  <>
                    You’ll join a community who will talk you down from the cookie binge and advise you on how to deal
                    with the meat and potatoes guilt trips from your friends and family. They get your fears (‘cause
                    they’ve got them too). And they’ll be there with the confetti for every one of your wins.
                  </>
                )}
              </p>
            </Col>
          </Row>
        </Container>
      </SectionImage>
      <Section id="course-details-coursebook" color="white">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-center">
                Outwit the Aspiration Trap with a Little Help From the Healthy Heart Coursebook
              </h2>
            </Col>
          </Row>
          <Row className="justify-content-center text-center">
            <Col lg="10" xl="8">
              <img
                className="mb-4"
                src="https://cdn.foodrevolution.org/hhc/hhc-coursebook.png"
                style={{maxHeight: '350px'}}
              />
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg="10">
              <p>
                {variant === 'ad' ? (
                  <>
                    Best intentions rarely translate into meaningful life changes — unless you have a way to truly take
                    in what you learn. That’s what this workbook is brilliantly designed to address.
                  </>
                ) : (
                  <>
                    Intentions and aspirations rarely translate into meaningful life changes — unless you have a way to
                    truly take in what you learn. That’s what this workbook is brilliantly designed to address.
                  </>
                )}
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    Absorb what you learn better as you commit it to heart (Pun intended!) and track your progress. It’s
                    amazing how much can be learned in a short period of time.
                  </>
                ) : (
                  <>
                    You’ll absorb what you learn better as you commit it to heart (Pun intended!) and track your
                    progress. You’ll be amazed at how much you improve in a short period of time.
                  </>
                )}
              </p>
            </Col>
          </Row>
        </Container>
      </Section>
      <SectionImage
        id="course-details-qa"
        color="light-gray"
        imgPosition="right"
        imgUrl={require('static/backgrounds/1252916139.jpg')}
      >
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading">
                {variant === 'ad' ? (
                  <>Trade in Heart-Health Confusion for “A-has!” With Our Expert Q&A</>
                ) : (
                  <>Trade in Heart Disease Riddles for “A-has!” With Our Expert Q&A</>
                )}
              </h2>
              <p>
                {variant === 'ad' ? (
                  <>
                    Feel like there are 8 answers to every one of your questions? Get conflicting info from doctor to
                    doctor? And forget about the Facebook comment section to the online blogs! What’s needed isn’t
                    there.
                  </>
                ) : (
                  <>
                    Feel like there are 8 answers to every one of your questions on heart health? Get conflicting info
                    from doctor to doctor? And forget about the Facebook comment section to the online blogs! You won’t
                    get what you need there.
                  </>
                )}
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    Unriddling heart questions shouldn’t be as tricky as a game of Sudoku. Get your answers from Dr.
                    Guarneri, the multi-award winning cardiologist with 20 years of experience helping patients,
                    naturally.
                  </>
                ) : (
                  <>
                    Unriddling heart disease shouldn’t be as tricky as a game of Sudoku. Get your burning questions
                    answered by Dr. Guarneri, the multi-award winning cardiologist with 20 years of experience helping
                    patients reset the course of heart disease, naturally.
                  </>
                )}
              </p>
              <p>
                It’s time for real answers to your questions and fears about heart health in our expert Q&A with Dr.
                Guarneri, hosted by Ocean Robbins. Pre-recorded and downloadable, with the written transcript if you
                like to read and take notes.
              </p>
            </Col>
          </Row>
        </Container>
      </SectionImage>
      <Section id="course-details-guests" color="white">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-center mb-0">
                Learn From the Best with 6 Bonus Expert Interviews from{' '}
                <span className="text-nowrap">Dr. Dean Ornish,</span>{' '}
                <span className="text-nowrap">Dr. Kim Williams,</span>{' '}
                <span className="text-nowrap">Dr. Joel Kahn,</span>{' '}
                <span className="text-nowrap">Dr. Saray Stancic,</span>{' '}
                <span className="text-nowrap">Dr. Aysha Akhtar,</span> and{' '}
                <span className="text-nowrap">Marco Borges</span>
              </h2>
              <p className="text-center m-0">
                <img className="my-4" src={require('static/course-details-interviews.png')} />
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg="10">
              <p>
                How can you undo past choices that keep you feeling{variant === 'ad' ? null : ' sick and'} run down?
                What does a vegan cardiologist eat for breakfast? And can a plant-based diet contribute to increased
                female libido? Discover the answers to all this and more inside each module’s bonus interview with the
                world’s top heart experts, including <i>New York Times</i> bestselling authors.
              </p>
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="course-details-title" color="green" innerClass="text-center text-white">
        <Container>
          <Row>
            <Col>
              <h2 className="section-only-heading text-uppercase">The Course Modules</h2>
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="course-modules" color="white">
        <Container>
          <Row>
            <Col>{variant === 'ad' ? <ModuleAccordion modulelist={modulesAds} /> : <ModuleAccordion />}</Col>
          </Row>
        </Container>
      </Section>

      <Section id="bonuses" color="light-gray">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-center">
                Join today to get over $400 in free gifts to level up your health
              </h2>
              <p className="text-center">
                Don’t miss 5 freebies for your heart, brain, weight, and health at every level.
              </p>
              {variant === 'ad' ? (
                <BonusOpen borderColor="medium-gray-50" bonuslist={bonusAds} />
              ) : (
                <BonusOpen borderColor="medium-gray-50" />
              )}
            </Col>
          </Row>
        </Container>
      </Section>

      {version === 'screening' ? (
        <Section id="screening-collection" color="white">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center">
                  Plus, when you join TODAY from this page only, you’ll get the bonus Heart Health Collection for FREE.
                  ($97 Value)
                </h2>
                <Row className="justify-content-center">
                  <Col lg="6">
                    <IconList className="mb-0">
                      <IconListItem>4 Powerful Interviews</IconListItem>
                      <IconListItem>Full Written Transcripts</IconListItem>
                      <IconListItem>6 Delicious Plant-Based Heart Healthy Recipes</IconListItem>
                      <IconListItem>The Heart Health Workbook</IconListItem>
                    </IconList>
                  </Col>
                </Row>
                <hr className="my-5" />
                {hhcollection.map((item) => (
                  <>
                    <div className="hhcollection-item">
                      <Row className="align-items-center mb-4">
                        <Col md="3" lg="2" className="text-center mb-4 mb-lg-0">
                          <img src={item.image} />
                        </Col>
                        <Col md="9" lg="10">
                          <ReactMarkdown className="hhcollection-title" source={item.title} />
                        </Col>
                      </Row>
                      <ReactMarkdown className="hhcollection-description" source={item.description} />
                    </div>
                    <hr className="my-5" />
                  </>
                ))}

                <h3 className="text-center">Remember… Today Only!</h3>
                <h5 className="text-center mt-0">
                  Get This Entire Heart Health Collection for Free When You Join the <i>Healthy Heart</i>
                </h5>
                <p className="text-center mb-0">A $97 gift, from us to you.</p>
              </Col>
            </Row>
          </Container>
        </Section>
      ) : null}

      <Section id="cta-2" color="gradient-indigo-purple-right">
        <Container>
          {children}
          {version === 'screening' ? (
            <Row className="text-center text-white mt-5">
              <Col>
                <h2 className="section-heading mb-3">Special Offer</h2>
                <p>
                  <i>Exclusively for Masterclass participants. From this page only.</i>
                </p>
                <h3 className="mt-4">Today's Special Early-Bird Enrollment Discount</h3>
                <p>
                  This early-bird discount is only for Masterclass attendees as a reward for investing an hour in this
                  mission-critical aspect of your health.
                </p>
                <p>
                  *You can{' '}
                  <b>
                    <i>only</i>
                  </b>{' '}
                  unlock your special price on this page.*
                </p>
                <p>(Do not hit refresh or close!)</p>
                <p>
                  <b>
                    With this Special Masterclass Price, you save $250 by enrolling in <i>Healthy Heart</i> today.
                  </b>
                </p>
                <p>
                  <b>The enrollment price is higher everywhere else on our site.</b>
                </p>
                <h5 className="mt-4">Unlimited Access. Enjoy it all as much as you want.</h5>
              </Col>
            </Row>
          ) : null}
        </Container>
      </Section>

      <Section id="testimonials-2" color="white">
        <Container>
          <Row className="justify-content-center">
            <Col>
              <TestimonialBubble id="hhc-maryann" />
              <hr className="my-5" />
              <TestimonialBubble id={variant === 'ad' ? 'hhc-byron-ad' : 'hhc-byron'} />
              <hr className="my-5" />
              <TestimonialBubble id={variant === 'ad' ? 'hhc-patricia-k-ad' : 'hhc-patricia-k'} />
              <hr className="my-5" />
              <TestimonialBubble id="hhc-mercy" />
              <hr className="my-5" />
              <TestimonialBubble id="pam-r" />
              <hr className="my-5" />
              <TestimonialBubble id="dennis-j" />
              <hr className="my-5" />
              <TestimonialBubble id="carol-k" />
              <hr className="my-5" />
              <TestimonialBubble id="r-singh" />
              <hr className="my-5" />
              <TestimonialBubble id="brad-s" />
              <hr className="my-5" />
              <TestimonialBubble id="brian-a" />
            </Col>
          </Row>
          <Row className="mt-5">
            <Col>
              <p className="text-small text-center mb-0">
                <i>
                  These stories are from people who’ve found success with our programs. Each of them is unique. And so
                  are you! Your experiences will vary, of course. And no, we never offer compensation for these
                  statements!
                </i>
              </p>
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="guarantee" color="light-gray">
        <Container>
          <Row>
            <Col>
              <GuaranteeBox title={<>You’re Protected By Our 60-Day, Unconditional, Money-Back Guarantee.</>}>
                {' '}
                <p>
                  <b>Try it out.</b> If you aren't completely blown away by the course, even if you just don’t like it,
                  just email us at <a href="mailto:support@foodrevolution.org">support@foodrevolution.org</a>, any time
                  within 60 days after purchase, for a prompt and courteous refund.
                </p>
                <p>
                  This means you can even take the whole course, take every lesson, and make every single mouth-watering
                  recipe, and if it doesn’t rock your world, you get every penny back.
                </p>
                <p>
                  That’s how confident we are that you’ll LOVE <i>Healthy Heart!</i>
                </p>
                <p>
                  PLUS, if for any reason you request a refund, the entire <i>Healthy Heart</i> course — everything that
                  you’ve downloaded, and everything you’ve learned — is still yours to keep.
                </p>
                <p className="mb-0">That’s why we call our guarantee “better than money back.”</p>
              </GuaranteeBox>
            </Col>
          </Row>
        </Container>
      </Section>

      <Section id="faq" color="white">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading">Frequently Asked Questions</h2>
              {version === 'screening' ? (
                <FaqList />
              ) : (
                <>{variant === 'ad' ? <FaqList faqlist={faqAds} /> : <FaqList slice={1} />}</>
              )}
            </Col>
          </Row>
        </Container>
      </Section>

      <SectionImage
        id="help"
        color="light-gray"
        imgPosition="left"
        imgUrl={require('static/backgrounds/547528530.jpg')}
        title={<h2 className="section-heading">Why You Absolutely Can’t Afford To Put This Off</h2>}
        titleClass="text-center mb-0"
      >
        <Container>
          <Row>
            <Col>
              {variant === 'ad' ? (
                <>
                  <p>Imagine you have two roads before you.</p>
                  <p>
                    One is filled with scary diagnoses, stressful hospital visits, a cabinet full of medications with
                    horrible side effects, expensive bills, insurance issues , and constant fear.
                  </p>
                  <p>
                    The other road is filled with joy, energy, gratitude, laughter, fun, and feeling connected — no
                    matter what life throws at you.
                  </p>
                  <p>Now imagine knowing exactly what to do, and having the positive community to help you do it.</p>
                  <p>The sooner you take action…</p>
                  <p>...the better your chances of living the best, happiest, longest life possible.</p>
                  <p>Plus, you’ll be able to pass what you learn onto your loved ones.</p>
                  <h4 className="mt-0">It’s worth it to protect yourself and your family.</h4>
                </>
              ) : (
                <>
                  {
                    {
                      prevent: (
                        <>
                          <p>Imagine you have two roads before you.</p>
                          <p>
                            One is filled with scary diagnoses, stressful hospital visits, a cabinet full of medications
                            with horrible side effects, chronic pain, expensive bills, insurance headaches, constant
                            fear, and even untimely death.
                          </p>
                          <p>
                            The other road is filled with joy, energy, gratitude, laughter, fun, and feeling connected —
                            no matter what life throws at you.
                          </p>
                          <p>
                            Now imagine knowing exactly what to do, and having the positive community to help you do it.
                          </p>
                          <p>
                            And while many of the stages of heart disease are reversible, the sooner you take action…
                          </p>
                          <p>...the better your chances of living the best, happiest, longest life possible.</p>
                          <p>Plus, you’ll be able to pass what you learn onto your loved ones.</p>
                          <h4 className="mt-0">
                            It’s worth it to protect yourself and your family from unnecessary heartache.
                          </h4>
                        </>
                      ),
                      optimize: (
                        <>
                          <p>There’s so much you want to do in your life.</p>
                          <p>
                            Maybe you want to be able to swim, explore new places, rung a half marathon, kayak, surf,
                            garden, keep up with kids or grandkids, go to museums and concerts, enjoy backyard parties,
                            have energy to dance late into the night at a wedding, or to wake up early to hike and watch
                            the sun rise…
                          </p>
                          <p>You don’t want to slow down or miss any of the things you love.</p>
                          <p>
                            And you know that getting your heart into the best shape possible NOW means you’ll be able
                            to stay fit and active for years.
                          </p>
                          <p>
                            But you also know that there’s tons of conflicting, outdated, and flat-out wrong
                            information.
                          </p>
                          <p>You don’t have time to waste going down the wrong road.</p>
                          <p>
                            Imagine knowing exactly what to do, and having the positive community to help you do it.
                          </p>
                          <p>
                            Wouldn’t you love to have anytime access to health coaches and nutritionists who can answer
                            your questions and point you in the right direction?
                          </p>
                          <p>
                            The sooner you take action… the better your chances of living the best, happiest, longest
                            life possible.
                          </p>
                          <p>Plus, you’ll be able to pass what you learn onto your loved ones.</p>
                          <h4 className="mt-0">An extraordinary life is waiting for you.</h4>
                        </>
                      ),
                      reverse: (
                        <>
                          <p>Imagine you have two roads before you.</p>
                          <p>
                            One is filled with scary diagnoses, stressful hospital visits, a cabinet full of medications
                            with horrible side effects, chronic pain, expensive bills, insurance headaches, constant
                            fear, and even untimely death.
                          </p>
                          <p>
                            The other road is filled with joy, energy, gratitude, laughter, fun, and feeling connected —
                            no matter what life throws at you.
                          </p>
                          <p>
                            Now imagine knowing exactly what to do, and having the positive community to help you do it.
                          </p>
                          <p>
                            And while many of the stages of heart disease are reversible, the sooner you take action…
                          </p>
                          <p>...the better your chances of living the best, happiest, longest life possible.</p>
                          <p>Plus, you’ll be able to pass what you learn onto your loved ones.</p>
                          <h4 className="mt-0">
                            It’s worth it to protect yourself and your family from unnecessary heartache.
                          </h4>
                        </>
                      )
                    }[bucket]
                  }
                </>
              )}
              <h3 className="mt-0">We’re here to help you.</h3>
              <h3 className="mt-0">Join us!</h3>
            </Col>
          </Row>
        </Container>
      </SectionImage>

      <Section id="cta-3" color="gradient-indigo-purple-right">
        <Container>{children}</Container>
      </Section>

      <Section id="references" color="light-gray" className="section-angle-last" angle="none">
        <Container>
          <Row className="d-flex justify-content-center">
            <Col>
              <h3 className="section-heading">References</h3>

              <ul className="list-references list-group text-small mb-0">
                <li className="list-group-item reference-item d-flex align-items-center p-3">
                  <div className="pr-3">[1]</div>
                  <div>
                    Ornish D, Brown SE, Scherwitz LW, et al. Can lifestyle changes reverse coronary heart disease? The
                    Lifestyle Heart Trial. Lancet. 1990;336(8708):129-133. doi:10.1016/0140-6736(90)91656-u
                  </div>
                </li>
                <li className="list-group-item reference-item d-flex align-items-center p-3">
                  <div className="pr-3">[2]</div>
                  <div>
                    Katz DL, Frates EP, Bonnet JP, Gupta SK, Vartiainen E, Carmona RH. Lifestyle as Medicine: The Case
                    for a True Health Initiative. Am J Health Promot. 2018;32(6):1452-1458. doi:10.1177/0890117117705949
                  </div>
                </li>
                <li className="list-group-item reference-item d-flex align-items-center p-3">
                  <div className="pr-3">[3]</div>
                  <div>
                    Keith F, Krantz DS, Chen R, et al. Anger, hostility, and hospitalizations in patients with heart
                    failure. Health Psychol. 2017;36(9):829-838. doi:10.1037/hea0000519
                  </div>
                </li>
                <li className="list-group-item reference-item d-flex align-items-center p-3">
                  <div className="pr-3">[4]</div>
                  <div>
                    Raič M. Depression and Heart Diseases: Leading Health Problems. Psychiatr Danub. 2017;29 Suppl
                    4(Suppl 4):770-777. https://pubmed.ncbi.nlm.nih.gov/29278623/
                  </div>
                </li>
              </ul>
            </Col>
          </Row>
        </Container>
      </Section>
    </>
  );
}

export function SalesCopyNav({...props}) {
  const [collapsed, setCollapsed] = useState(true);
  const toggleNavbar = () => setCollapsed(!collapsed);

  function StickyNavLink({scrollToRef, linkText}) {
    return (
      <NavItem>
        <Link
          className="hhc-nav-list-item"
          activeClass="active"
          spy={true}
          offset={-55}
          to={scrollToRef}
          smooth={true}
          href="#"
        >
          {linkText}
        </Link>
      </NavItem>
    );
  }
  return (
    <div className={classNames(`hhc-nav`, props.className)}>
      <Container>
        <Navbar className={classNames(`p-0`, `navbar-light`, `justify-content-end`)} expand="lg">
          <div className="d-lg-none text-dark-gray">Quick Navigation</div>
          <NavbarToggler onClick={toggleNavbar} />
          <Collapse className="justify-content-center" isOpen={!collapsed} navbar>
            <Nav navbar>
              <StickyNavLink scrollToRef="why" linkText="Why This Program" />
              <StickyNavLink scrollToRef="modules" linkText="Modules" />
              <StickyNavLink scrollToRef="bonuses" linkText="Bonuses" />
              <StickyNavLink scrollToRef="instructors" linkText="Instructors" />
              <StickyNavLink scrollToRef="faqs" linkText="FAQs" />
              <StickyNavLink scrollToRef="purchase" linkText="Buy Now" />
            </Nav>
          </Collapse>
        </Navbar>
      </Container>
    </div>
  );
}

export function SalesCopyV1({version, variant, bucket, children, ...props}) {
  function SalesCtaSection({id, className = undefined, type = 'triangle', ...props}) {
    return (
      <SectionCL id={id} color="purple" className={className} type={type}>
        <Container>
          <Row>
            <Col>{children}</Col>
          </Row>
        </Container>
      </SectionCL>
    );
  }
  return (
    <>
      <SalesCopyNav />
      <div className="why">
        <SectionCL id="" color="white" type="triangle" innerClass="pt-5">
          <Container>
            <Row className="text-center mb-5">
              <Col>
                <p className="mb-2">
                  <i>Introducing</i>
                </p>
                <p className="mb-4">
                  <img className="img-course-logo" src="https://cdn.foodrevolution.org/hhc/hhc-logo.svg" />
                </p>
                <p className="balance-text mb-0">
                  Gain energy, eat better, stress less, and love more — for your heart’s sake!
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col lg="10">
                <h3 className="section-heading">
                  {variant === 'ad' ? (
                    <>
                      <i>Healthy Heart</i> is for anyone who wants to:
                    </>
                  ) : (
                    <>
                      <i>Healthy Heart</i> is for you if you want to:
                    </>
                  )}
                </h3>
                {
                  {
                    default: (
                      <IconListCL>
                        <>Have more time, energy, and motivation to do the things you love.</>
                        <>Be your healthiest so you can spend quality time with your family and friends.</>
                        <>
                          Discover the impressive ways your heart stores emotions and how to reduce stress, depression,
                          and anger — and instead become one of the happiest people you know. This is based on evidence
                          and the latest research!
                        </>
                        <>
                          Eat better, lose any excess weight, and feel more confident in your choices without ever
                          feeling deprived or stuck with a diet of bland food.
                        </>
                        <>
                          {
                            {
                              prevent: (
                                <>
                                  Enjoy healthy, plant-based recipes based on foods that can help you slash your risk of
                                  heart disease.
                                </>
                              ),
                              optimize: (
                                <>
                                  Enjoy healthy, plant-based recipes based on foods that can help you give your heart
                                  every advantage.
                                </>
                              ),
                              reverse: (
                                <>
                                  Enjoy healthy, plant-based recipes based on foods that can help you prevent and even
                                  reverse heart disease.
                                </>
                              )
                            }[bucket]
                          }
                        </>
                        <>
                          {
                            {
                              prevent: (
                                <>
                                  Ensure you’re not missing any of the key elements that help you to optimize your heart
                                  health — and enjoy being able to do the activities you love without fear of heart
                                  trouble slowing you down prematurely.
                                </>
                              ),
                              optimize: (
                                <>
                                  Ensure you’re not missing any of the key elements that help you to optimize your heart
                                  health… and enjoy the activities you love without fear of heart trouble slowing you
                                  down prematurely.
                                </>
                              ),
                              reverse: (
                                <>
                                  Ensure you’re not missing any of the key elements that help you to optimize your heart
                                  health — and enjoy being able to do the activities you love without heart trouble
                                  robbing you of quality years.
                                </>
                              )
                            }[bucket]
                          }
                        </>
                        <>
                          Find a community of supportive, health-minded friends who encourage you to make healthy
                          choices and cheer you on.
                        </>
                        <>
                          Get answers to your heart health questions whenever you wonder about nutrition, dangerous
                          oils, exercise, zapping stress, how to get back on track, or even when you need a little
                          inspiration.
                        </>
                        <>Protect yourself and your family from unnecessary heartache.</>
                      </IconListCL>
                    ),
                    ad: (
                      <IconListCL>
                        <>Have more time, energy, and motivation to do the things you love.</>
                        <>Be healthy and spend more quality time with family and friends.</>
                        <>
                          Discover the impressive ways your heart stores emotions and how to reduce stress, sadness and
                          anger — and instead become one of the happiest people you know. This is based on evidence and
                          the latest research!
                        </>
                        <>
                          Eat better, lose any excess weight, and feel more confident - without feeling deprived or
                          stuck with a diet of bland food.
                        </>
                        <>
                          Enjoy healthy, plant-based recipes based on delicious foods that can help keep the heart
                          happy.
                        </>
                        <>
                          Ensure you’re not missing any of the key elements that help to optimize your heart health —
                          and continue to enjoy favorite activities without fear of slowing down prematurely.
                        </>
                        <>
                          Find a community of supportive, health-minded friends who encourage you to make healthy
                          choices and cheer you on.
                        </>
                        <>
                          Get answers to heart health questions whenever you wonder about nutrition, exercise, zapping
                          stress, how to get back on track, or even for just a little inspiration.
                        </>
                        <>Protect yourself and your family from unnecessary heartache.</>
                      </IconListCL>
                    )
                  }[variant]
                }
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="light-gray" type="triangle">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center">Success Stories From the Healthy Heart Community</h3>
                <p className="balance-text text-center text-small mb-5">
                  <i>
                    These stories are from people who’ve found success with our programs. Each of them is unique. And so
                    are you! Your experiences will vary, of course. And no, we never offer compensation for these
                    statements!
                  </i>
                </p>
                <TestimonialCarousel
                  list={[
                    variant === 'ad' ? 'hhc-lori-r-ad' : 'hhc-lori-r',
                    'hhc-laura',
                    'hhc-karen-e',
                    variant === 'ad' ? 'hhc-delores-r-ad' : 'hhc-delores-r',
                    'jorge-m',
                    'cheryl-c',
                    'badiyyah-w'
                  ]}
                />
              </Col>
            </Row>
          </Container>
        </SectionCL>
        {version === 'screening' && (
          <>
            <SectionCL id="" color="white" type="triangle" className="text-center">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-heading">About Today’s Special Enrollment Discount</h3>
                    <p className="balance-text">
                      This special discount is only for Masterclass attendees — as a reward for investing an hour in
                      your growth by watching the Masterclass. You can unlock a special price only on this page. With
                      this Special Masterclass Price, you save $250 by enrolling in Healthy Heart today.
                    </p>
                    <p className="balance-text">
                      <b className="text-purple">IMPORTANT!</b> Only order from this page to get our best offer. The
                      enrollment price is higher everywhere else on our site.
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="" color="black" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-only-heading text-center text-white">
                      Why Order Today: 3 Powerful Advantages Only Available Now
                    </h2>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="" color="white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <IconListCL iconColor="purple" iconAlignment="top" number>
                      <>
                        <h5 className="section-heading text-purple mb-3">
                          The Lowest Price That Will Ever Be Offered.
                        </h5>
                        <p>
                          The regular price for Healthy Heart is $497. And for all that it includes, that’s already a
                          huge bang for your buck! But when you enroll today, you'll invest only $247 — this is a
                          MASSIVE $250 discount. (Prefer a three-month payment plan? We’ve got you covered! Join now for
                          just $89/month!)
                        </p>
                        <p>
                          You just chose to invest your time in our Masterclass, and this means you’re probably already
                          one of the special people who know that food matters and who’s willing to eat differently, and
                          to think differently, than the vast majority of the people around you. You’re exactly the kind
                          of person we created the Healthy Heart Course for: someone who’s open to learning, motivated
                          to act and inspired to create a future free from the fear of heart disease.
                        </p>
                        <p>So we want to make it easy for you to say yes, and to say yes, now.</p>
                      </>
                      <>
                        <h5 className="section-heading text-purple mb-3">
                          Get the Heart Health Collection as a totally free bonus
                        </h5>
                        <p>
                          As part of this special offer, you’ll receive the Heart Health Collection—a curated resource
                          designed to empower and inspire you on your journey to optimal heart health. This exclusive
                          bonus includes expert insights from world-renowned cardiologists like Dr. Caldwell Esselstyn,
                          Dr. Joel Kahn, Dr. Kim Williams, and Dr. Mimi Guarneri, who share proven strategies for
                          protecting and strengthening your heart. You’ll also enjoy complete transcripts of these
                          enlightening interviews, six quick and delicious heart-healthy recipes, and the Heart Food
                          Guidebook, which introduces you to 15 superfoods your heart will love. To help you apply what
                          you’ve learned, the collection also features a comprehensive workbook packed with tools to
                          track your progress and implement life-changing strategies. This $97 value is completely free
                          when you sign up today—your heart and future will thank you!
                        </p>
                      </>
                      <>
                        <h5 className="section-heading text-purple mb-3">
                          Join With Other Participants Who Are Ready Now, Too
                        </h5>
                        <p>
                          The best way to ensure that your healthy intentions become reality is to join a community on
                          the same mission as you!
                        </p>
                        <IconListCL iconColor="purple">
                          <>
                            Receive answers from our health coaches and nutritionists anytime you have a question — this
                            is worth the cost of the program alone!
                          </>
                          <>Get the latest health updates and groundbreaking scientific studies.</>
                          <>
                            Share recipes, struggles, and successes, and stay inspired by those who share your goal to
                            be healthy.
                          </>
                        </IconListCL>
                        <p>
                          This is the warmest and most engaged moderated online community you can find. Your success
                          depends on the company you keep!
                        </p>
                      </>
                    </IconListCL>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SalesCtaSection id="" />
            <SectionCL id="" color="black" className="text-center text-white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-heading text-center mb-3">
                      What’s Included in <i>Healthy Heart</i>
                    </h3>
                    <p className="balance-text text-center mb-0">
                      {
                        {
                          default: (
                            <>
                              Now you can get everything you need to help protect and nourish your cardiovascular health
                              with <i>Healthy Heart.</i>
                            </>
                          ),
                          ad: (
                            <>
                              Get everything needed to help nourish cardiovascular health with <i>Healthy Heart.</i>
                            </>
                          )
                        }[variant]
                      }
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="" color="white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    {/*enrollItems.map((item, i) => {
                      if (
                        version === 'sales' &&
                        item.text ===
                          '**BONUS (exclusive to this page): Heart Health Collection**  \nGain access to 20 of the most impactful interviews featuring John Robbins and the world’s top food and health experts. Discover the latest, science-backed insights to safeguard your health and make empowered choices for a vibrant future.'
                      ) {
                        return null;
                      }
                      return (
                        <div
                          key={'enrollItem' + i}
                          className={classNames(`enroll-item`, `p-4`, i != 0 && 'border-top')}
                        >
                          <Row className="align-items-center justify-content-center">
                            <Col xs="8" md="3" lg="2" className="mb-4 mb-md-0">
                              <img src={item.image.url} alt={item.image.alt} />
                            </Col>
                            <Col md="9" lg="10" className="enroll-item-text text-center text-md-left">
                              <ReactMarkdown source={item.text} />
                            </Col>
                          </Row>
                        </div>
                      );
                    })*/}
                    <IconListCL iconAlignment="top">
                      {enrollItems.map((item, i) => {
                        if (
                          version === 'sales' &&
                          item.text ===
                            '**BONUS (exclusive to this page): Heart Health Collection**  \nGain access to 20 of the most impactful interviews featuring John Robbins and the world’s top food and health experts. Discover the latest, science-backed insights to safeguard your health and make empowered choices for a vibrant future.'
                        ) {
                          return null;
                        }
                        return (
                          <div key={'enrollItem' + i}>
                            <ReactMarkdown source={item.text} />
                          </div>
                        );
                      })}
                    </IconListCL>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="" color="black" innerClass="text-center text-white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-only-heading text-uppercase">The Course Details</h2>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="" color="white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading text-center">
                      36 In-Depth Video Lessons on All Things Heart Health
                    </h2>
                  </Col>
                </Row>
                <Row className="justify-content-center text-center">
                  <Col lg="10" xl="8">
                    <img src="https://cdn.foodrevolution.org/hhc/hhc-videos.png" style={{maxHeight: '350px'}} />
                  </Col>
                </Row>
                <Row className="justify-content-center">
                  <Col lg="10">
                    <p>
                      {variant === 'ad' ? (
                        <>
                          Heart health goes way beyond diet and exercise habits (and most mainstream practitioners are
                          only trained to give out pills). We believe in an integrative approach to heart health that
                          most doctors’ offices don’t share — and probably don’t even know about.
                        </>
                      ) : (
                        <>
                          Your heart health goes way beyond your diet and exercise habits (and most mainstream
                          practitioners are only trained to give out pills). We believe in an integrative approach to
                          heart health that you won’t hear about in most doctors’ offices.
                        </>
                      )}
                    </p>
                    <p>
                      Get 6 video lessons in each of the 6 modules (most with run times from 5–20 minutes), taught by
                      Dr. Mimi Guarneri, President of the Academy of Integrative Health and Medicine.
                    </p>
                    <p>
                      It’s super easy to absorb everything. Like Netflix, but better, since this information could
                      literally {variant === 'ad' ? 'change' : 'save'} your life.
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="course-details-coaches" color="light-gray" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading text-center mb-0">
                      {variant === 'ad' ? (
                        <>Take Control of Heart Health with Our Certified Health Coaches & Nutritionists</>
                      ) : (
                        <>
                          Take Control of Heart Health and Reduce Your Risk with Our Certified Health Coaches &
                          Nutritionists
                        </>
                      )}
                    </h2>
                  </Col>
                </Row>
                <Row className="justify-content-center text-center">
                  <Col lg="10" xl="8">
                    <img className="my-4" src={require('static/course-details-coaches.png')} />
                  </Col>
                </Row>
                <Row className="justify-content-center">
                  <Col lg="10">
                    <p>
                      A life of statins, surgery, and {variant === 'ad' ? 'fear' : 'anxiety'} that things will only get
                      worse... Many people with a family history{variant === 'ad' ? null : ' of heart problems'} feel
                      like their fate begins and ends with their doctor.
                    </p>
                    <p>
                      {variant === 'ad' ? (
                        <>
                          How different things become when you realize <b>you</b> have the power!
                        </>
                      ) : (
                        <>How different things become when you realize you have the power to improve your own health!</>
                      )}
                    </p>
                    <p>
                      {variant === 'ad' ? (
                        <>
                          Our certified health and nutrition coaches are ready to help with tips and guidance to support
                          heart health. Get ongoing support any time a question or new health concern comes up — even
                          after the course is over!
                        </>
                      ) : (
                        <>
                          Our certified health and nutrition coaches are ready to help you so you can protect your heart
                          and feel amazing! That’s why we included a way for you to have ongoing support any time a
                          question or new health concern comes up — even after the course is over!
                        </>
                      )}
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="course-details-recipes" color="white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading text-center">
                      36 of Dr. Guarneri’s favorite heart-healthy plant-based recipes
                    </h2>
                  </Col>
                </Row>
                <Row className="text-center">
                  <Col>
                    <p className="balance-text">
                      Learn how to cook nourishing plant-based meals with recipes and expert demos from Food Revolution
                      Network’s Registered Dietitian Nutritionist.
                    </p>
                    <p className="balance-text">
                      Each module has a cooking demo video, along with 6 easy, heart-healthy dishes including…
                    </p>
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col lg="6" className="mb-4">
                    <RecipeTileBox id="avocado-salad" />
                  </Col>
                  <Col lg="6" className="mb-4">
                    <RecipeTileBox id="zesty-turmeric-ginger-carrot-cake" />
                  </Col>
                  <Col lg="6" className="mb-4">
                    <RecipeTileBox id="mushroom-and-pepper-scramble-tofu" />
                  </Col>
                  <Col lg="6" className="mb-4">
                    <RecipeTileBox id="spiced-moroccan-vegetable-soup" />
                  </Col>
                  <Col lg="6" className="mb-4 mb-lg-0">
                    <RecipeTileBox id="fluffy-buckwheat-chia-pancakes" />
                  </Col>
                  <Col lg="6" className="mb-0">
                    <RecipeTileBox id="chickpeas-and-peas-vegan-curry" />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <h3 className="text-center">And so many more!</h3>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL
              id="course-details-community"
              color="light-gray"
              imgPosition="left"
              imgUrl={require('static/backgrounds/1188864563.jpg')}
              type="triangle"
            >
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading">An Online Community Like No Other</h2>
                    <p>
                      {variant === 'ad' ? (
                        <>None of us can keep up with heart-happy lifestyle changes without the right social support.</>
                      ) : (
                        <>
                          Time for the bad news: you won’t keep up with your heart-happy lifestyle changes without the
                          right social support.
                        </>
                      )}
                    </p>
                    <p>
                      The upside? {variant === 'ad' ? 'Find' : 'You’ll find'} your people inside the{' '}
                      <i>Healthy Heart Course.</i>
                    </p>
                    <p>
                      {variant === 'ad' ? (
                        <>
                          Join a community who will talk you down from the cookie binge and help you navigate the meat
                          and potatoes guilt trips from your friends and family. They “get” your fears (‘cause they’ve
                          got them too). And they’ll be there with the confetti for every one of your wins.
                        </>
                      ) : (
                        <>
                          You’ll join a community who will talk you down from the cookie binge and advise you on how to
                          deal with the meat and potatoes guilt trips from your friends and family. They get your fears
                          (‘cause they’ve got them too). And they’ll be there with the confetti for every one of your
                          wins.
                        </>
                      )}
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="course-details-coursebook" color="white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading text-center">
                      Outwit the Aspiration Trap with a Little Help From the Healthy Heart Coursebook
                    </h2>
                  </Col>
                </Row>
                <Row className="justify-content-center text-center">
                  <Col lg="10" xl="8">
                    <img
                      className="mb-4"
                      src="https://cdn.foodrevolution.org/hhc/hhc-coursebook.png"
                      style={{maxHeight: '350px'}}
                    />
                  </Col>
                </Row>
                <Row className="justify-content-center">
                  <Col lg="10">
                    <p>
                      {variant === 'ad' ? (
                        <>
                          Best intentions rarely translate into meaningful life changes — unless you have a way to truly
                          take in what you learn. That’s what this workbook is brilliantly designed to address.
                        </>
                      ) : (
                        <>
                          Intentions and aspirations rarely translate into meaningful life changes — unless you have a
                          way to truly take in what you learn. That’s what this workbook is brilliantly designed to
                          address.
                        </>
                      )}
                    </p>
                    <p>
                      {variant === 'ad' ? (
                        <>
                          Absorb what you learn better as you commit it to heart (Pun intended!) and track your
                          progress. It’s amazing how much can be learned in a short period of time.
                        </>
                      ) : (
                        <>
                          You’ll absorb what you learn better as you commit it to heart (Pun intended!) and track your
                          progress. You’ll be amazed at how much you improve in a short period of time.
                        </>
                      )}
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL
              id="course-details-qa"
              color="light-gray"
              imgPosition="right"
              imgUrl={require('static/backgrounds/1252916139.jpg')}
              type="triangle"
            >
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading">
                      {variant === 'ad' ? (
                        <>Trade in Heart-Health Confusion for “A-has!” With Our Expert Q&A</>
                      ) : (
                        <>Trade in Heart Disease Riddles for “A-has!” With Our Expert Q&A</>
                      )}
                    </h2>
                    <p>
                      {variant === 'ad' ? (
                        <>
                          Feel like there are 8 answers to every one of your questions? Get conflicting info from doctor
                          to doctor? And forget about the Facebook comment section to the online blogs! What’s needed
                          isn’t there.
                        </>
                      ) : (
                        <>
                          Feel like there are 8 answers to every one of your questions on heart health? Get conflicting
                          info from doctor to doctor? And forget about the Facebook comment section to the online blogs!
                          You won’t get what you need there.
                        </>
                      )}
                    </p>
                    <p>
                      {variant === 'ad' ? (
                        <>
                          Unriddling heart questions shouldn’t be as tricky as a game of Sudoku. Get your answers from
                          Dr. Guarneri, the multi-award winning cardiologist with 20 years of experience helping
                          patients, naturally.
                        </>
                      ) : (
                        <>
                          Unriddling heart disease shouldn’t be as tricky as a game of Sudoku. Get your burning
                          questions answered by Dr. Guarneri, the multi-award winning cardiologist with 20 years of
                          experience helping patients reset the course of heart disease, naturally.
                        </>
                      )}
                    </p>
                    <p>
                      It’s time for real answers to your questions and fears about heart health in our expert Q&A with
                      Dr. Guarneri, hosted by Ocean Robbins. Pre-recorded and downloadable, with the written transcript
                      if you like to read and take notes.
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="course-details-guests" color="white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading text-center mb-0">
                      Learn From the Best with 6 Bonus Expert Interviews from{' '}
                      <span className="text-nowrap">Dr. Dean Ornish,</span>{' '}
                      <span className="text-nowrap">Dr. Kim Williams,</span>{' '}
                      <span className="text-nowrap">Dr. Joel Kahn,</span>{' '}
                      <span className="text-nowrap">Dr. Saray Stancic,</span>{' '}
                      <span className="text-nowrap">Dr. Aysha Akhtar,</span> and{' '}
                      <span className="text-nowrap">Marco Borges</span>
                    </h2>
                    <p className="text-center m-0">
                      <img className="my-4" src={require('static/course-details-interviews.png')} />
                    </p>
                  </Col>
                </Row>
                <Row className="justify-content-center">
                  <Col lg="10">
                    <p>
                      How can you undo past choices that keep you feeling{variant === 'ad' ? null : ' sick and'} run
                      down? What does a vegan cardiologist eat for breakfast? And can a plant-based diet contribute to
                      increased female libido? Discover the answers to all this and more inside each module’s bonus
                      interview with the world’s top heart experts, including <i>New York Times</i> bestselling authors.
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
          </>
        )}
      </div>
      <div className="modules">
        <SectionCL id="" color="black" innerClass="text-center text-white" type="triangle">
          <Container>
            <Row>
              <Col>
                <h2 className="section-only-heading text-uppercase">The Course Modules</h2>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="white" type="triangle">
          <Container>
            <Row>
              <Col>{variant === 'ad' ? <ModuleAccordion modulelist={modulesAds} /> : <ModuleAccordion />}</Col>
            </Row>
          </Container>
        </SectionCL>
      </div>
      <div className="bonuses">
        <SectionCL id="course-details-title" color="black" innerClass="text-center text-white" type="triangle">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center">
                  Join today to get over $400 in free gifts to level up your health
                </h2>
                <p className="text-center">
                  Don’t miss 5 freebies for your heart, brain, weight, and health at every level.
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="white" type="triangle">
          <Container>
            <Row>
              <Col>
                {variant === 'ad' ? (
                  <BonusAccordion borderColor="medium-gray-50" bonuslist={bonusAds} />
                ) : (
                  <BonusAccordion borderColor="medium-gray-50" />
                )}
              </Col>
            </Row>
          </Container>
        </SectionCL>
        {version === 'screening' && (
          <SectionCL id="" color="light-gray" type="triangle">
            <Container>
              <Row>
                <Col>
                  <h3 className="section-heading text-center">
                    Plus, when you join TODAY from this page only, you’ll get the bonus Heart Health Collection for
                    FREE. ($97 Value)
                  </h3>
                  <Row className="justify-content-center">
                    <Col lg="6">
                      <IconListCL className="mb-0">
                        <>4 Powerful Interviews</>
                        <>Full Written Transcripts</>
                        <>6 Delicious Plant-Based Heart Healthy Recipes</>
                        <>The Heart Health Workbook</>
                      </IconListCL>
                    </Col>
                  </Row>
                  <hr className="my-5" />
                  {hhcollection.map((item) => (
                    <>
                      <div className="hhcollection-item">
                        <Row className="align-items-center mb-4">
                          <Col md="3" lg="2" className="text-center mb-4 mb-lg-0">
                            <img src={item.image} />
                          </Col>
                          <Col md="9" lg="10">
                            <ReactMarkdown className="hhcollection-title" source={item.title} />
                          </Col>
                        </Row>
                        <ReactMarkdown className="hhcollection-description" source={item.description} />
                      </div>
                      <hr className="my-5" />
                    </>
                  ))}
                  <h3 className="text-center">Remember… Today Only!</h3>
                  <h5 className="text-center mt-0">
                    Get This Entire Heart Health Collection for Free When You Join the <i>Healthy Heart</i>
                  </h5>
                  <p className="text-center mb-0">A $97 gift, from us to you.</p>
                </Col>
              </Row>
            </Container>
          </SectionCL>
        )}
        <SectionCL id="" color={version === 'screening' ? 'white' : 'light-gray'} type="triangle">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center">Is Healthy Heart really worth it?</h3>
                <p className="balance-text text-center text-small mb-5">
                  <i>
                    These stories are from people who’ve found success with our programs. Each of them is unique. And so
                    are you! Your experiences will vary, of course. And no, we never offer compensation for these
                    statements!
                  </i>
                </p>
                <TestimonialCarousel
                  bubbleColor={version === 'screening' ? 'light-gray' : 'white'}
                  list={[
                    'hhc-maryann',
                    variant === 'ad' ? 'hhc-byron-ad' : 'hhc-byron',
                    variant === 'ad' ? 'hhc-patricia-k-ad' : 'hhc-patricia-k',
                    'hhc-mercy',
                    'pam-r',
                    'dennis-j',
                    'carol-k',
                    'r-singh',
                    'brad-s',
                    'brian-a'
                  ]}
                />
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SalesCtaSection id="" />
      </div>
      <div className="instructors">
        <SectionCL id="" color="white" type="triangle">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center mb-4">Meet Your Instructor</h2>
                <Row className="align-items-stretch justify-content-center">
                  <InstructorCardList />
                </Row>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id=""
          color="black"
          imgUrl="https://cdn.foodrevolution.org/global/images/175234125.jpg"
          angle="none"
          type="triangle"
        >
          <Container>
            <Row>
              <Col>
                <div className="background-white rounded box-shadow-black-50 p-3 p-lg-5 text-center text-lg-left">
                  <Row className="d-flex align-items-center justify-content-center mb-lg-4">
                    <Col xs="12" sm="8" lg="4" xl="3" className="order-lg-2 text-center mb-4 mb-lg-0">
                      <img
                        className="img-ocean-note rounded-circle"
                        src="https://cdn.foodrevolution.org/hhc/images/ocean-robbins.jpg"
                      />
                    </Col>
                    <Col xs="12" lg="8" xl="9" className="order-lg-1">
                      <h2 className="section-heading mb-4">A personal note from Ocean Robbins</h2>
                      <h4 className="my-4">
                        In his 70s, my Grandpa Irv (who founded Baskin-Robbins ice cream company) became seriously
                        ill...
                      </h4>
                    </Col>
                  </Row>
                  <Row className="d-flex align-items-center justify-content-center">
                    <Col xs="12" sm="8" lg="4" xl="3" className="img-ocean-note-icecream-row text-center">
                      <img
                        className="img-ocean-note img-ocean-note-icecream img-fluid img-rotate-left"
                        src="https://cdn.foodrevolution.org/hhc/images/irvine-with-ice-cream.jpg"
                      />
                    </Col>
                    <Col xs="12" lg="8" xl="9">
                      <p>
                        <b>
                          He’d always eaten the standard American diet, plus a double scoop of ice cream on top, and now
                          he was suffering from the standard American diseases.
                        </b>
                      </p>
                      <h5 className="my-4">That was his wake-up call.</h5>
                      <p>
                        His doctor told him that he'd have to take seven medications three times every day for the rest
                        of his life and that unless he changed his diet, he likely had only a few years to live — in
                        poor health.
                      </p>
                      <p>And then his doctor gave Grandpa Irv a book and told him to read it.</p>
                    </Col>
                  </Row>
                  <p>
                    The book was already familiar to him because it was written by his son (my dad), John Robbins. But
                    he hadn’t read it.
                  </p>
                  <p>
                    You see, there were some hard feelings in the family. Decades earlier, my grandpa had been grooming
                    my dad, John, to one day join in the family business.
                  </p>
                  <p>
                    But when he was in his early 20’s, my dad wound up walking away from Baskin-Robbins and the wealth
                    it represented because it didn't align with his values of health and compassion.
                  </p>
                  <p>
                    My dad left that legacy to follow his own "rocky road" and become a best-selling food and health
                    author. The media had fun with the story, calling him the “rebel without a cone.”
                  </p>
                  <p>
                    Millions of people were inspired by his work — including, as fate would have it, Grandpa Irv’s
                    doctor, who was now recommending my dad's best-selling book, Diet for a New America, to his patient.
                  </p>
                  <Row className="d-flex justify-content-center">
                    <Col xs="12" lg="7" xl="8">
                      <p>And amazingly enough, Grandpa Irv read the book and made some big changes.</p>
                      <p>
                        He added lots of vegetables and fruits; he went plant-powered, and he even gave up ice cream!
                      </p>
                      <h4 className="my-4">
                        My Grandpa Irv ended up getting off all his medications and adding almost 20 more healthy years
                        to his life.
                      </h4>
                    </Col>
                    <Col xs="12" sm="8" lg="5" xl="4" className="img-ocean-note-suits-row text-center">
                      <img
                        className="img-ocean-note img-ocean-note-suits img-fluid img-rotate-right"
                        src="https://cdn.foodrevolution.org/hhc/images/irvine-john-ocean.jpg"
                      />
                    </Col>
                  </Row>
                  <Row className=" d-flex justify-content-center align-items-center">
                    <Col xs="12" lg="8" className="order-lg-2">
                      <p>
                        The great news is that this is the kind of transformation I know is possible for anyone,
                        including you and your loved ones.
                      </p>
                      <h4 className="my-4">That's how POWERFUL food is.</h4>
                      <p>
                        My dad and I have spent decades living our passion to empower millions of people with this
                        life-saving knowledge.
                      </p>
                    </Col>
                    <Col xs="12" sm="8" lg="4" className="img-ocean-note-flexing-row text-center order-lg-1">
                      <img
                        className="img-ocean-note img-ocean-note-flexing img-fluid rounded-circle"
                        src="https://cdn.foodrevolution.org/hhc/images/john-ocean-square.jpg"
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="modern-medicine"
          color="white"
          imgPosition="right"
          imgUrl={require('static/backgrounds/1150397417.jpg')}
          angle="both"
          innerClass="pb-3"
          type="triangle"
          angleBottomDisplay="none"
        >
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading">Modern Medicine is Failing Us</h2>
                {variant === 'ad' ? (
                  <>
                    <p>
                      Despite trillions of dollars in medical spending, heart health issues are on the rise — claiming
                      the lives of people <i>decades</i> before their time.
                    </p>
                    <p>
                      Even though diet and lifestyle are the foundation of health, hospitals still serve burgers to
                      patients who are recovering from surgery.
                    </p>
                    <p>
                      And with all the focus on treating the symptoms, painfully little attention is given to helping
                      you stay healthy in the first place.
                    </p>
                    <p>People are robbed of enjoying their golden years because of:</p>
                    <IconListCL iconColor="yellow" icon={faExclamationTriangle} className="mb-0">
                      <>High stress...</>
                      <>The wrong foods...</>
                      <>Extra weight...</>
                      <>Toxins in household products...</>
                      <>Pollution and pesticides...</>
                      <>A sedentary lifestyle...</>
                      <>Even a lack of gratitude and spiritual connection...</>
                    </IconListCL>
                  </>
                ) : (
                  <>
                    {
                      {
                        prevent: (
                          <>
                            <p>
                              Despite trillions of dollars in medical spending, heart attacks, high cholesterol, and
                              obesity are on the rise — claiming the lives of people <i>decades</i> before their time.
                            </p>
                            <p>
                              People are robbed of enjoying their golden years because of the factors that lead to heart
                              disease, including:
                            </p>
                            <IconListCL iconColor="yellow" icon={faExclamationTriangle} className="mb-0">
                              <>High stress...</>
                              <>The wrong foods...</>
                              <>Extra weight...</>
                              <>Toxins in household products...</>
                              <>Pollution and pesticides...</>
                              <>A sedentary lifestyle...</>
                              <>Even a lack of gratitude and spiritual connection...</>
                            </IconListCL>
                          </>
                        ),
                        optimize: (
                          <>
                            <p>
                              Despite advances in Western medicine, it falls short when it comes to truly optimizing
                              your heart health.
                            </p>
                            <p>
                              Doctors are trained to prescribe expensive drugs and surgeries, but most have learned next
                              to nothing about nutrition.
                            </p>
                            <p>
                              Even though diet and lifestyle are the foundation of health, hospitals still serve burgers
                              to patients who are recovering from bypass surgery.
                            </p>
                            <p>
                              And with all the focus on treating the symptoms of disease, painfully little attention is
                              given to helping you enjoy the health and vitality you deserve.
                            </p>
                            <p>
                              Even if you’re doing a lot of things right, you might be missing one or more of the keys
                              to heart health due to misinformation and slick marketing.
                            </p>
                            <IconListCL iconColor="yellow" icon={faExclamationTriangle} className="mb-0">
                              <>Staying stressed...</>
                              <>Eating a few (or a lot) of the wrong foods...</>
                              <>Keeping toxic household products around...</>
                              <>Ingesting food with pesticides...</>
                              <>Exercising in a way that causes inflammation (the root of most disease)...</>
                            </IconListCL>
                          </>
                        ),
                        reverse: (
                          <>
                            <p>
                              Despite trillions of dollars in medical spending, heart attacks, high cholesterol, and
                              obesity are on the rise — claiming the lives of people <i>decades</i> before their time.
                            </p>
                            <p>
                              People are robbed of enjoying their golden years because of the factors that lead to heart
                              disease, including:
                            </p>
                            <IconListCL iconColor="yellow" icon={faExclamationTriangle} className="mb-0">
                              <>High stress...</>
                              <>The wrong foods...</>
                              <>Extra weight...</>
                              <>Toxins in household products...</>
                              <>Pollution and pesticides...</>
                              <>A sedentary lifestyle...</>
                              <>Even a lack of gratitude and spiritual connection...</>
                            </IconListCL>
                          </>
                        )
                      }[bucket]
                    }
                  </>
                )}
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="modern-medicine-b" color="white" type="triangle" innerClass="pt-0">
          <Container>
            <Row>
              <Col xl="11">
                {variant === 'ad' ? (
                  <>
                    <p>
                      But there’s <i>great</i> news.
                    </p>
                    <p>
                      A breakthrough study called the Lifestyle Heart Trial (which Dr. Mimi helped to implement) showed
                      that when patients made a few lifestyle changes for one year, 82% of participants were able to
                      experience significant changes.
                    </p>
                    <p>
                      Dozens of subsequent studies have shown how powerful lifestyle is to keep hearts healthy!
                      <sup>(1)</sup>
                    </p>
                  </>
                ) : (
                  <>
                    {
                      {
                        prevent: (
                          <>
                            <p>
                              But there’s <i>great</i> news.
                            </p>
                            <p>
                              A breakthrough study called the Lifestyle Heart Trial (which Dr. Mimi helped to implement)
                              showed that when heart disease patients made a few lifestyle changes for one year, 82% of
                              participants were able to partially or fully REVERSE their heart disease. And dozens of
                              subsequent studies have shown how powerful lifestyle is to prevent heart disease in the
                              first place!<sup>(1)</sup>
                            </p>
                            <p>That means it’s not too late to protect yourself from heart disease!</p>
                          </>
                        ),
                        optimize: (
                          <>
                            <p>
                              These seemingly small things add up and could be keeping you from high energy levels and a
                              strong heart you want.
                            </p>
                            <p>This is a big deal — there are so many things you want to do now and in the future!</p>
                          </>
                        ),
                        reverse: (
                          <>
                            <p>
                              But there’s <i>great</i> news.
                            </p>
                            <p>
                              A breakthrough study called the Lifestyle Heart Trial (which Dr. Mimi helped to implement)
                              showed that when heart disease patients made a few lifestyle changes for one year, 82% of
                              participants were able to partially or fully REVERSE their heart disease.
                              <sup>(1)</sup>
                            </p>
                            <p>That means it’s not too late to turn your heart health around!</p>
                          </>
                        )
                      }[bucket]
                    }
                  </>
                )}
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="help"
          color="light-gray"
          imgPosition="left"
          imgUrl={require('static/backgrounds/547528530.jpg')}
          type="triangle"
        >
          <h3 className="section-heading">Why You Absolutely Can’t Afford To Put This Off</h3>
          {variant === 'ad' ? (
            <>
              <p>Imagine you have two roads before you.</p>
              <p>
                One is filled with scary diagnoses, stressful hospital visits, a cabinet full of medications with
                horrible side effects, expensive bills, insurance issues , and constant fear.
              </p>
              <p>
                The other road is filled with joy, energy, gratitude, laughter, fun, and feeling connected — no matter
                what life throws at you.
              </p>
              <p>Now imagine knowing exactly what to do, and having the positive community to help you do it.</p>
              <p>The sooner you take action…</p>
              <p>...the better your chances of living the best, happiest, longest life possible.</p>
              <p>Plus, you’ll be able to pass what you learn onto your loved ones.</p>
              <h4 className="mt-0">It’s worth it to protect yourself and your family.</h4>
            </>
          ) : (
            <>
              {
                {
                  prevent: (
                    <>
                      <p>Imagine you have two roads before you.</p>
                      <p>
                        One is filled with scary diagnoses, stressful hospital visits, a cabinet full of medications
                        with horrible side effects, chronic pain, expensive bills, insurance headaches, constant fear,
                        and even untimely death.
                      </p>
                      <p>
                        The other road is filled with joy, energy, gratitude, laughter, fun, and feeling connected — no
                        matter what life throws at you.
                      </p>
                      <p>
                        Now imagine knowing exactly what to do, and having the positive community to help you do it.
                      </p>
                      <p>And while many of the stages of heart disease are reversible, the sooner you take action…</p>
                      <p>...the better your chances of living the best, happiest, longest life possible.</p>
                      <p>Plus, you’ll be able to pass what you learn onto your loved ones.</p>
                      <h4 className="mt-0">
                        It’s worth it to protect yourself and your family from unnecessary heartache.
                      </h4>
                    </>
                  ),
                  optimize: (
                    <>
                      <p>There’s so much you want to do in your life.</p>
                      <p>
                        Maybe you want to be able to swim, explore new places, rung a half marathon, kayak, surf,
                        garden, keep up with kids or grandkids, go to museums and concerts, enjoy backyard parties, have
                        energy to dance late into the night at a wedding, or to wake up early to hike and watch the sun
                        rise…
                      </p>
                      <p>You don’t want to slow down or miss any of the things you love.</p>
                      <p>
                        And you know that getting your heart into the best shape possible NOW means you’ll be able to
                        stay fit and active for years.
                      </p>
                      <p>
                        But you also know that there’s tons of conflicting, outdated, and flat-out wrong information.
                      </p>
                      <p>You don’t have time to waste going down the wrong road.</p>
                      <p>Imagine knowing exactly what to do, and having the positive community to help you do it.</p>
                      <p>
                        Wouldn’t you love to have anytime access to health coaches and nutritionists who can answer your
                        questions and point you in the right direction?
                      </p>
                      <p>
                        The sooner you take action… the better your chances of living the best, happiest, longest life
                        possible.
                      </p>
                      <p>Plus, you’ll be able to pass what you learn onto your loved ones.</p>
                      <h4 className="mt-0">An extraordinary life is waiting for you.</h4>
                    </>
                  ),
                  reverse: (
                    <>
                      <p>Imagine you have two roads before you.</p>
                      <p>
                        One is filled with scary diagnoses, stressful hospital visits, a cabinet full of medications
                        with horrible side effects, chronic pain, expensive bills, insurance headaches, constant fear,
                        and even untimely death.
                      </p>
                      <p>
                        The other road is filled with joy, energy, gratitude, laughter, fun, and feeling connected — no
                        matter what life throws at you.
                      </p>
                      <p>
                        Now imagine knowing exactly what to do, and having the positive community to help you do it.
                      </p>
                      <p>And while many of the stages of heart disease are reversible, the sooner you take action…</p>
                      <p>...the better your chances of living the best, happiest, longest life possible.</p>
                      <p>Plus, you’ll be able to pass what you learn onto your loved ones.</p>
                      <h4 className="mt-0">
                        It’s worth it to protect yourself and your family from unnecessary heartache.
                      </h4>
                    </>
                  )
                }[bucket]
              }
            </>
          )}
          <h3 className="mt-0">We’re here to help you.</h3>
          <h3 className="mt-0">Join us!</h3>
        </SectionCL>
        <SalesCtaSection id="" />
        <SectionCL id="guarantee" color="light-gray" type="triangle">
          <Container>
            <Row>
              <Col>
                <GuaranteeBox title={<>You’re Protected By Our 60-Day, Unconditional, Money-Back Guarantee.</>}>
                  {' '}
                  <p>
                    <b>Try it out.</b> If you aren't completely blown away by the course, even if you just don’t like
                    it, just email us at <a href="mailto:support@foodrevolution.org">support@foodrevolution.org</a>, any
                    time within 60 days after purchase, for a prompt and courteous refund.
                  </p>
                  <p>
                    This means you can even take the whole course, take every lesson, and make every single
                    mouth-watering recipe, and if it doesn’t rock your world, you get every penny back.
                  </p>
                  <p>
                    That’s how confident we are that you’ll LOVE <i>Healthy Heart!</i>
                  </p>
                  <p>
                    PLUS, if for any reason you request a refund, the entire <i>Healthy Heart</i> course — everything
                    that you’ve downloaded, and everything you’ve learned — is still yours to keep.
                  </p>
                  <p className="mb-0">That’s why we call our guarantee “better than money back.”</p>
                </GuaranteeBox>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>
      <div className="faqs">
        <SectionCL id="faq" color="white" type="triangle">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading">Frequently Asked Questions</h2>
                {version === 'screening' ? (
                  <FaqList />
                ) : (
                  <>{variant === 'ad' ? <FaqList faqlist={faqAds} /> : <FaqList slice={1} />}</>
                )}
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>
      <div className="purchase">
        <SalesCtaSection id="" />
        <SectionCL id="references" color="light-gray" angle="none" order="last">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col>
                <h3 className="section-heading">References</h3>

                <ul className="list-references list-group text-small mb-0">
                  <li className="list-group-item reference-item d-flex align-items-center p-3">
                    <div className="pr-3">[1]</div>
                    <div>
                      Ornish D, Brown SE, Scherwitz LW, et al. Can lifestyle changes reverse coronary heart disease? The
                      Lifestyle Heart Trial. Lancet. 1990;336(8708):129-133. doi:10.1016/0140-6736(90)91656-u
                    </div>
                  </li>
                  <li className="list-group-item reference-item d-flex align-items-center p-3">
                    <div className="pr-3">[2]</div>
                    <div>
                      Katz DL, Frates EP, Bonnet JP, Gupta SK, Vartiainen E, Carmona RH. Lifestyle as Medicine: The Case
                      for a True Health Initiative. Am J Health Promot. 2018;32(6):1452-1458.
                      doi:10.1177/0890117117705949
                    </div>
                  </li>
                  <li className="list-group-item reference-item d-flex align-items-center p-3">
                    <div className="pr-3">[3]</div>
                    <div>
                      Keith F, Krantz DS, Chen R, et al. Anger, hostility, and hospitalizations in patients with heart
                      failure. Health Psychol. 2017;36(9):829-838. doi:10.1037/hea0000519
                    </div>
                  </li>
                  <li className="list-group-item reference-item d-flex align-items-center p-3">
                    <div className="pr-3">[4]</div>
                    <div>
                      Raič M. Depression and Heart Diseases: Leading Health Problems. Psychiatr Danub. 2017;29 Suppl
                      4(Suppl 4):770-777. https://pubmed.ncbi.nlm.nih.gov/29278623/
                    </div>
                  </li>
                </ul>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>
    </>
  );
}
